import * as THREE from 'three';
import { Engine } from './core/Engine.js';
import { Input } from './core/Input.js';
import { Physics } from './physics/Physics.js';
import { PostFX } from './render/PostFX.js';
import { Atmosphere } from './world/Atmosphere.js';
import { Level } from './world/Level.js';
import { CrossfireLevel } from './world/CrossfireLevel.js';
import { AssaultLevel } from './world/AssaultLevel.js';
import { Dust2Level } from './world/Dust2Level.js';
import { PlayerController } from './player/PlayerController.js';
import { Weapon, WEAPONS } from './player/Weapon.js';
import { Effects } from './fx/Effects.js';
import { Enemy } from './ai/Enemy.js';
import { NavGrid } from './ai/NavGrid.js';
import {
  applySkill, DEFAULT_DIFFICULTY, DIFFICULTIES, difficultyById, savedDifficulty, saveDifficulty,
} from './ai/Difficulty.js';
import { NetClient } from './net/NetClient.js';
import { NetSession } from './net/NetSession.js';
import { Capsule } from 'three/addons/math/Capsule.js';
import { HUD } from './ui/HUD.js';
import { AudioEngine } from './audio/Audio.js';
import { Bomb } from './gameplay/Bomb.js';
import { Killstreaks } from './gameplay/Killstreaks.js';
import { Spectator } from './gameplay/Spectator.js';
import { OperatorStage } from './ui/OperatorStage.js';
import { MatchAwards } from './ui/MatchAwards.js';
import { QUALITY_PRESETS, applyQuality, savedQuality, saveQuality, qualityById } from './render/Quality.js';
import { operatorFor, OPERATORS, ALL_OPERATORS } from './gameplay/operators.js';
import { BOMB_RULES, EXPLOSIVES } from './modes/rules.js';
import { pelletPattern, damageAtRange, zoneMultiplier } from './player/ballistics.js';
import { Deathmatch } from './modes/Deathmatch.js';
import { BombDefusal } from './modes/BombDefusal.js';

/**
 * Which audio voice a weapon fires with. Derived from what the weapon *is*
 * rather than hard-coded per name, so a new entry in the WEAPONS table gets a
 * sensible report without also having to be listed here. An explicit
 * `spec.sound` always wins.
 */
function voiceFor(spec) {
  if (spec.sound) return spec.sound;
  if (spec.projectile) return 'launcher';
  if ((spec.pellets ?? 1) > 1) return 'shotgun';
  if (spec.mag <= 20 && spec.mode === 'semi' && spec.damage < 40) return 'pistol';
  if (spec.mode === 'semi') return 'dmr';
  if (spec.rpm >= 850) return 'smg';
  return 'rifle';
}

const _v = new THREE.Vector3();
/** Scratch for the spawn jitter search; no allocation in a respawn. */
const _spawnJitter = new THREE.Vector3();
const _dir = new THREE.Vector3();
const _muzzle = new THREE.Vector3();

class Game {
  /**
   * Levels may load asynchronously, and a level needs the Engine (for the
   * renderer, when generating textures) before it can be built. So the Engine
   * is constructed first and handed in alongside the finished level.
   */
  static async create(container, mapDef, opts = {}) {
    const engine = new Engine(container);
    const level = await mapDef.build(engine);
    return new Game(container, engine, level, { mapDef, ...opts });
  }

  constructor(container, engine, level, opts = {}) {
    // `mode` and `team` are consumed in a later phase; stored now so the
    // selection screens are wired end to end and testable on their own.
    this.opts = opts;
    this.modeId = opts.mode ?? 'dm';
    this.playerTeam = opts.team ?? 'blue';
    /**
     * The operator this player picked. Cosmetic, but it is what the award
     * screen puts on the podium, so the game has to remember it even though
     * nothing in the first-person view ever shows it.
     */
    this.playerOp = Number.isFinite(opts.op) ? opts.op : 0;
    /**
     * Online session, or null offline. When present it OWNS the local player's
     * movement and there are no local bots — the server decides everything that
     * matters and this process just draws it.
     */
    this.net = null;
    this._netClient = opts.net ?? null;
    this.mapDef = opts.mapDef ?? null;
    this.engine = engine;
    this.input = new Input(this.engine.renderer.domElement);
    this.physics = new Physics();

    this.level = level;
    this.engine.scene.add(this.level.root);
    this.physics.build(this.level.collision);
    // The map's own standing-player hull, when the payload has one. Not
    // collision — a validator for "is this somewhere the level allowed".
    this.physics.clipHull = this.level.clipHull ?? null;
    // Levels with moving parts (doors, lifts) and ladders register them now
    // that Physics exists — they can't be built before it.
    this.level.attach?.(this.physics);

    // A level may declare its own lighting mood (see AssaultLevel).
    this.atmosphere = new Atmosphere(this.engine, this.level.constructor.atmosphere ?? {});
    // Always the full stack, whatever the preset. Presets toggle passes rather
    // than skipping their construction, so the setting can be changed live —
    // see src/render/Quality.js.
    this.postfx = new PostFX(this.engine, { quality: 'high' });
    this.engine.onResize = (w, h) => this.postfx.resize(w, h);
    /** Graphics preset id. The Escape menu changes it; Quality.js applies it. */
    this.quality = savedQuality();
    applyQuality(this, this.quality);

    this.player = new PlayerController(this.physics, this.input, this.engine.camera);
    // Spawn where the level says to. Crossfire's spawns come straight from the
    // map's info_player_deathmatch entities; the urban block has its own list.
    const spawn = this.level.playerSpawn
      ?? this.level.spawnPoints?.[0]
      ?? new THREE.Vector3(0, 0.3, 34);
    this.player.teleport(spawn.x, spawn.y + 0.1, spawn.z);
    this.player.yaw = this.level.playerSpawnYaw ?? Math.PI;

    this.effects = new Effects(this.engine, this.physics);
    this.effects.onShellBounce = (pos, speed) => this.audio.shellDrop(pos, speed);

    this.weapon = new Weapon(this.engine, this.player, WEAPONS.m4);
    this.weapon.onReloadStart = () => {
      this.audio.reload('out');
      setTimeout(() => this.audio.reload('in'), 900);
      setTimeout(() => this.audio.reload('bolt'), 1600);
    };

    this.hud = new HUD(container);
    this.audio = new AudioEngine(this.engine.camera);

    this.enemies = [];
    /*
     * Bot difficulty. SINGLEPLAYER ONLY — online the server owns the bots, and
     * a client-side skill setting there would be either a desync or a cheat, so
     * the menu hides it and this value is never sent.
     */
    this.difficulty = this._netClient ? DEFAULT_DIFFICULTY : savedDifficulty();
    // The player is on blue; deathmatch bots default to red, so today's
    // behaviour (everyone hostile to the player) is unchanged.
    this.player.team = this.playerTeam;
    this.world = {
      physics: this.physics, effects: this.effects, scene: this.engine.scene,
      player: this.player, level: this.level, audio: this.audio,
      enemies: this.enemies,
      // Offline, only bomb mode dresses the sides differently — deathmatch
      // keeps the mixed desert palettes it has always had. ONLINE, both modes
      // are team games and every body you see belongs to a side, so team
      // colours always apply: online team deathmatch had everyone in the same
      // desert kit, which is unplayable for exactly the reason the palettes
      // exist.
      teamsMatter: (opts.mode ?? 'dm') === 'bomb' || !!this._netClient,
      // Navigation, so the AI can plan a route instead of steering at a
      // straight line. `world` is a facade over the Game rather than the Game
      // itself, so anything the bots need has to be handed across explicitly —
      // `world.nav` being quietly undefined is a silent fallback to the old
      // whisker-only behaviour, which is exactly the bug being fixed.
      nav: () => this.nav(),
      navPoints: () => this.navPoints(),
      coverPoints: () => this.coverPoints(),
      difficulty: () => this.difficulty,
      onPlayerHit: (from) => this._onPlayerHit(from),
      findTarget: (me) => this._findTarget(me),
      hitscanVictim: (o, d, max, shooter) => this._hitscanVictim(o, d, max, shooter),
      onShotFired: (pos, team, shooter) => {
        Enemy.hearShot?.(pos, team);
        this.mode?.stats?.onShot?.(shooter);
      },
      /**
       * Damage, for the offline award stats. Online this never fires — bots do
       * not exist there and every hit is resolved on the server.
       */
      onDamage: (attacker, victim, amount, part) =>
        this.mode?.stats?.onDamage?.(attacker, victim, amount, part),
    };
    /**
     * The end-of-match award screens. Owned by the Game rather than the HUD
     * because both the online session and the offline mode decide when it
     * appears, and neither of them can reach into the HUD.
     */
    this.matchAwards = new MatchAwards();
    /** The operator model the local player is wearing, for the award podium. */
    this.playerOperator = operatorFor(this.playerOp,
      { team: this.playerTeam, ffa: !this.world.teamsMatter });
    // Stamped on the controller too, because that is the actor MatchStats sees:
    // without it the player's own award page put them on the podium in slot 01
    // whatever they had actually picked.
    this.player.operator = this.playerOperator;
    this.enemyId = 0;

    // Loadout order for the number keys / scroll wheel, smallest to largest:
    // melee, sidearm, then shotgun, SMG, rifle, marksman, launcher. Declaration
    // order in the WEAPONS table is authoring order and reads arbitrarily in
    // game, so the slot order is stated explicitly here. Anything in WEAPONS
    // that isn't listed still gets a slot, appended at the end.
    const SLOT_ORDER = ['knife', 'm9', 'm870', 'mp5', 'm4', 'dmr', 'rpg'];
    const byKey = new Map(Object.entries(WEAPONS));
    this.slots = SLOT_ORDER.filter((k) => byKey.has(k)).map((k) => byKey.get(k));
    for (const [k, spec] of byKey) {
      if (!SLOT_ORDER.includes(k)) this.slots.push(spec);
    }
    this.rockets = [];
    this.throwables = [];
    // Grenade ballistics. Exposed as fields rather than literals so they can be
    // swept by tools/nade-probe.mjs — range goes as roughly the square of the
    // launch speed once drag is involved, which is not a number worth guessing.
    // 15.5 m/s put a level throw 6.3 m away — barely past the muzzle. 28.5
    // lands it at 11.3 m, the requested +80%, measured to first ground contact
    // (where it stops rolling is not a throw distance).
    this.throwSpeed = 28.5;
    // Was 2.4. A large constant loft lifts every throw by the same amount and
    // drowns out the aim; at 1.2 the arc is the player's to choose — level
    // throws 11 m, 30 degrees up throws 24 m, 30 down throws 3.
    this.throwLoft = 1.2;
    this.flash = 0;
    // Real counts, so the HUD row stops advertising equipment that isn't there.
    // Deliberately NOT bound to a number key: 1-9 select weapons.
    this.equipment = {
      frag: { key: 'G', kind: 'frag', n: 2, max: 2 },
      flash: { key: 'Q', kind: 'flash', n: 2, max: 2 },
    };

    this.footstepAccum = 0;
    this.started = false;
    this.paused = true;

    this.input.onLockChange = (locked) => {
      this.paused = !locked;
      if (locked) { this.audio.init(); this.started = true; this._hidePauseMenu(); }
      // Escape releases pointer lock. Before, that left you looking at a frozen
      // frame with a "click to resume" hint and no way out of a match except
      // reloading the tab — so Escape now opens a real menu.
      else if (this.started) this._showPauseMenu();
    };

    this.player.onLand = (impact) => this.audio.footstep(this.player.position, 0.6 + impact);
    this.player.onJump = () => this.audio.footstep(this.player.position, 0.4);
    this.player.onDeath = (dir) => this._onPlayerDeath(dir);
    // Being pulled back into the world has to be explained, or it reads as the
    // teleport bug it is protecting you from.
    this.player.onRecover = () => {
      this.hud.showObjective('OUT OF BOUNDS', 'returned to the combat area', 2.6);
      this.player.addTrauma(0.25);
    };
    this.respawnTimer = 0;
    this.deaths = 0;

    this.bomb = this.modeId === 'bomb' ? new Bomb(this) : null;
    this.streaks = new Killstreaks(this);
    this.spectator = new Spectator(this);
    this.clockNow = 0;
    this.progress = null;

    // The mode owns spawning, round state and win conditions; Game keeps
    // owning the simulation. Deathmatch is the path every regression probe
    // drives, so it stays a plain object doing exactly what it always did.
    if (this._netClient) {
      this.playerTeam = this._netClient.team ?? this.playerTeam;
      // `player.team` was stamped from `playerTeam` in the constructor, BEFORE
      // the server's answer was read — so online it kept the default and every
      // remote player looked hostile regardless of side. Restated here.
      this.player.team = this.playerTeam;
      this.net = new NetSession(this._netClient, this);
      // Place the local player where the server put them, or the first frame
      // is a metre-scale correction. `spawn` is the EYE position.
      const sp = this._netClient._welcomeSpawn;
      if (sp) {
        this.player.capsule.end.set(sp[0], sp[1], sp[2]);
        this.player.capsule.start.set(sp[0], sp[1] - (this.player.height - this.player.capsule.radius * 2), sp[2]);
        this.player.velocity.set(0, 0, 0);
      }
    }

    this.mode = this.modeId === 'bomb'
      ? new BombDefusal(this, opts)
      : new Deathmatch(this, opts);
    // Offline only. Online, the SERVER owns the match — spawning local bots
    // here would put AI in the world that no other player can see and that the
    // server has never heard of.
    if (!this.net) this.mode.start();

    this._loop = this._loop.bind(this);
    requestAnimationFrame(this._loop);
  }

  /**
   * @param at explicit spawn position. Honoured exactly — the ±4 m scatter is
   *   only for picking a random spawn point, and applying it to a caller's
   *   explicit coordinate makes placement non-deterministic (which silently
   *   broke a test that thought it controlled where bots stood).
   */
  /**
   * A spawn point that is random but not on top of anybody.
   *
   * Picking uniformly at random from `spawnPoints` looks fine on paper and is
   * awful in play: on Dust II every one of the forty spawns sits in the T half,
   * so a blind pick regularly dropped the player inside the bot pack and they
   * were dead before the fade finished. Distance to the nearest living actor is
   * the thing that actually matters, so it is what gets measured.
   *
   * Everything past SAFE is equally good, and the choice among those is random
   * — otherwise "spawn furthest away" turns into "always spawn in the same
   * corner", which is just as predictable as the bug it replaced.
   */
  /**
   * Candidate spawn positions.
   *
   * In a team mode the authored `spawnPoints` are exactly right — they are the
   * two start rooms, which is the point. In a free-for-all they are exactly
   * wrong: on Dust II they are two huddles of twenty, one per team room, 89 m
   * apart with nothing in between, so deathmatch dropped all eight bots and the
   * player into two corners of the map and called it a spawn.
   *
   * So a free-for-all spawns off the walkable grid instead, which covers the
   * whole level. Built once and cached on the level; if it cannot be built the
   * authored points are still there to fall back on.
   */
  /**
   * The level's walkable grid — ONE per level, shared by everything that needs
   * to know where a body can stand.
   *
   * There used to be two: `_spawnCandidates` built a 2.5 m grid, used it to
   * pick spawn points and threw it away, and bomb defusal built a separate
   * 1.25 m one for its routes. Deathmatch bots, meanwhile, had no navigation at
   * all — they steered at a straight line toward a random spawn point with
   * 2.6 m whiskers, which is what "на месте стоят, в стены тыкаются" is. The
   * grid needed to cross the map already existed and was being discarded.
   *
   * 1.25 m, matching what bomb mode measured as usable. It costs a few thousand
   * raycasts, so it is built once, lazily, and cached on the LEVEL rather than
   * the game — a round restart must not pay for it again.
   *
   * Returns null when the level has no collision to sample, and every caller
   * has to cope: the authored spawn points and reactive steering are still
   * there as the fallback.
   */
  nav() {
    if (this.level._navGrid === undefined) {
      try {
        const box = new THREE.Box3().setFromObject(this.level.collision);
        this.level._navGrid = box.isEmpty() ? null
          : new NavGrid(this.physics, box.expandByScalar(2), 1.25);
      } catch { this.level._navGrid = null; }
    }
    return this.level._navGrid;
  }

  /**
   * A thinned list of places a body can stand, for the AI to patrol between.
   *
   * Thinned hard and cached on the level: a 1.25 m grid over Dust II is tens of
   * thousands of cells, and `_patrolPoint` samples this list several times per
   * destination per bot. 600 points is far more coverage than eight bots can
   * use and costs one array.
   */
  navPoints() {
    if (this.level._navPoints === undefined) {
      const nav = this.nav();
      let pts = null;
      try {
        const all = nav ? nav.walkablePoints() : null;
        if (all?.length) {
          const step = Math.max(1, Math.ceil(all.length / 600));
          pts = all.filter((_, i) => i % step === 0);
        }
      } catch { pts = null; }
      this.level._navPoints = pts?.length ? pts : null;
    }
    return this.level._navPoints;
  }

  /**
   * Barriers the AI can fight from behind.
   *
   * The level's own authored list where it has one, and a list derived from the
   * walkable grid where it does not. The converted Counter-Strike maps are the
   * second case and it is not a near miss: `dust2.json` carries no POI entities
   * at all and `assault.json` carries four, all of them the hostages in one
   * room. `_acquireCover` therefore always failed there, so two of the four
   * squad roles and every suppressed bot behaved as if cover did not exist on
   * half the map pool.
   *
   * Both lists, on every map, rather than the derived one only as a fallback.
   * Authored points are better data and there are never enough of them:
   * blackout authors 107 over a 339 m span, which sounds generous and leaves a
   * bot in a firefight with nothing inside the 9 m `_acquireCover` will walk
   * to. Density is what decides whether the state is reachable at all, and the
   * selection filters are strict enough that a weak candidate costs a rejected
   * raycast rather than a bad decision.
   */
  coverPoints() {
    if (this.level._coverPoints === undefined) {
      const own = this.level.coverPoints ?? [];
      let derived = null;
      try { derived = this.nav()?.coverPoints() ?? null; } catch { derived = null; }
      const pts = derived?.length ? [...own, ...derived] : own;
      this.level._coverPoints = pts.length ? pts : null;
    }
    return this.level._coverPoints;
  }

  _spawnCandidates() {
    if (!this.mode?.constructor?.ffa) return this.level.spawnPoints ?? [];
    if (this.level._dmSpawns === undefined) {
      let pts = null;
      try {
        const nav = this.nav();
        if (nav) {
          const all = nav.walkablePoints();
          // Lift off the floor by the capsule radius so nothing spawns clipped
          // into the ground, and thin the list — a 1.25 m grid over Dust II is
          // tens of thousands of cells and the picker scores every one of them.
          if (all.length > 40) {
            const step = Math.ceil(all.length / 400);
            pts = all.filter((_, i) => i % step === 0);
          } else pts = all;
          // Walkable does not mean standable: a grid cell centre can sit hard
          // against a wall or under a low ceiling, and spawning there puts the
          // capsule inside the level. Every candidate is tested with the real
          // player capsule and the failures are dropped, once, at build time.
          const c = this.player.capsule;
          const h = this.player.height;
          const probe = new Capsule(new THREE.Vector3(), new THREE.Vector3(), c.radius);
          // Lifted clear of the surface before testing. A capsule resting
          // exactly on the floor registers as overlapping it, which rejected
          // 95% of the map — 276 candidates on Dust II fell to 15 and the
          // spread collapsed from 100 m to 28 m. The player settles onto the
          // floor a moment after spawning anyway.
          const LIFT = 0.10;
          pts = pts.filter((p) => {
            probe.start.set(p.x, p.y + c.radius + LIFT, p.z);
            probe.end.set(p.x, p.y + h - c.radius + LIFT, p.z);
            return !this.physics.overlaps(probe);
          });
        }
      } catch { pts = null; }
      this.level._dmSpawns = pts?.length ? pts : null;
    }
    return this.level._dmSpawns ?? this.level.spawnPoints ?? [];
  }

  /**
   * Everyone in the world except you: bots offline, remote players online.
   *
   * `enemies` is the list of bots THIS process simulates, and online it is
   * deliberately always empty — remote players are rigs in `_netBodies`, which
   * the game never ticks. Half a dozen systems read `enemies` directly and so
   * quietly did nothing in a network game: the reticle never identified anyone,
   * spectating had nobody to watch, the minimap showed no contacts, and the UAV
   * revealed an empty map. Each surfaced as a separate bug report and each got
   * a separate patch, which is how you end up fixing the same thing five times.
   *
   * The two lists never coexist by construction — `spawnEnemy` refuses to run
   * online — so the common cases return the underlying array with no copy and
   * no shared-scratch hazard. The merge is a safety net, not a hot path.
   */
  get actors() {
    const net = this._netBodies;
    if (!net?.length) return this.enemies;
    if (!this.enemies.length) return net;
    const out = (this._actorsMerged ??= []);
    out.length = 0;
    for (const e of this.enemies) out.push(e);
    for (const e of net) out.push(e);
    return out;
  }

  /** Everyone on the other side. Your own team is not a contact. */
  foes(out) {
    const list = out;
    list.length = 0;
    const mine = this.player.team;
    for (const e of this.actors) {
      if (mine && e.team === mine) continue;
      list.push(e);
    }
    return list;
  }

  _pickSpawn(exclude = null) {
    const pts = this._spawnCandidates();
    if (!pts.length) return new THREE.Vector3(0, 0.3, 34);

    const actors = [];
    if (this.player.alive && this.player !== exclude) actors.push(this.player.position);
    for (const e of this.enemies) {
      if (e.alive && e !== exclude && !e._removed) actors.push(e.root.position);
    }

    const SAFE = 18;                       // metres; a comfortable engagement gap
    const scored = pts.map((p) => {
      let d = Infinity;
      for (const a of actors) d = Math.min(d, p.distanceTo(a));
      return { p, d };
    });
    scored.sort((a, b) => b.d - a.d);
    const best = scored[0].d;

    // Prefer everything comfortably clear, but only if that is a real choice —
    // a pool of one is a fixed spawn, not a random one, and on the small maps
    // the SAFE threshold really does leave exactly one point.
    //
    // Otherwise fall back to a floor RELATIVE to the best available. That is
    // the important part: an absolute fallback ("take the roomiest few") padded
    // the pool with whatever came next, and on Al-Rasheed that meant a 2.6 m
    // spawn was drawn while a 28 m one was sitting there unused. Nowhere truly
    // safe is the normal case on the BSP maps — Dust II's best point is 6 m
    // from a bot and its median under 3 m — so the floor scales with what the
    // map can actually offer, and spawn protection covers the rest.
    const safe = scored.filter((s) => s.d >= SAFE);
    const pool = safe.length >= 3 ? safe : scored.filter((s) => s.d >= best * 0.6);
    const chosen = pool[(Math.random() * pool.length) | 0];

    /*
     * Jitter, scored rather than applied blind.
     *
     * Several bots leaving the same point stack, so the position gets a small
     * random offset — but that offset used to be added AFTER the choice, by the
     * caller, and it can move a spawn up to 2.83 m in the worst direction. On a
     * small map that is most of the margin: Crossfire's floor works out around
     * 11 m and the probe measured separations as low as 9.2 m, which is the
     * jitter walking a legal spawn into an illegal one. The comment there
     * claimed it was "well under the safety margin"; measured, it was not.
     *
     * Offering a handful of offsets and keeping the roomiest gets the
     * anti-stacking without ever making the choice worse.
     */
    const out = chosen.p.clone();
    if (actors.length) {
      let bestD = chosen.d;
      const cand = _spawnJitter;
      for (let i = 0; i < 6; i++) {
        cand.copy(chosen.p);
        cand.x += (Math.random() - 0.5) * 4;
        cand.z += (Math.random() - 0.5) * 4;
        let d = Infinity;
        for (const a of actors) d = Math.min(d, cand.distanceTo(a));
        if (d > bestD) { bestD = d; out.copy(cand); }
      }
    }
    return out;
  }

  /**
   * A body for a remote player: the soldier rig with no brain attached.
   *
   * Reuses Enemy purely for its mesh so other players look like everything else
   * in the game, then never ticks it. Their decisions were made on another
   * machine; running AI on top would fight the snapshots that position them.
   */
  /** Reverse-lookup a weapon's key in the WEAPONS table, for the wire. */
  weaponKeyFor(spec) {
    for (const [k, v] of Object.entries(WEAPONS)) if (v === spec) return k;
    return 'm4';
  }

  spawnNetBody(team = 'red', op = 0) {
    // A remote player wears the operator THEY picked, not one derived from an
    // id this process invented for their rig.
    const e = new Enemy(this.world, this.level.spawnPoints[0].clone(), this.enemyId++, team, {
      ffa: !!this.mode?.constructor?.ffa,
      operator: operatorFor(op, { team, ffa: !this.world.teamsMatter }),
    });
    e.alive = true;
    // Not in `this.enemies`: that list is what the game ticks, damages and
    // hitscans against, and none of those are this process's business online.
    this._netBodies ??= [];
    this._netBodies.push(e);
    return e;
  }

  despawnNetBody(rig) {
    rig.despawn?.();
    const i = this._netBodies?.indexOf(rig) ?? -1;
    if (i >= 0) this._netBodies.splice(i, 1);
  }

  spawnEnemy(team = 'red', at = null) {
    // Belt and braces. Online the server owns every actor; a bot spawned here
    // would be visible only to this player, shootable by nobody, and unknown to
    // the server. Refusing at the source means no future code path can
    // reintroduce it by accident.
    if (this.net) return null;
    // The anti-stacking jitter lives inside _pickSpawn now, where it can be
    // scored against everyone else's position instead of blindly undoing the
    // choice it was applied on top of.
    const p = at ? at.clone() : this._pickSpawn();
    // Free-for-all is a property of the mode, not of the bot, but `isFoe` is
    // asked on the bot — and so is which palette it wears, which is decided
    // while the model is built. Both go in at construction.
    const ffa = !!this.mode?.constructor?.ffa;
    const e = new Enemy(this.world, p, this.enemyId++, team, { ffa });
    e.onDeath = (victim, killer) => {
      if (this.bomb?.carrier === e) this.bomb.drop(e.root.position.clone());
      // Credit and feedback belong to whoever actually got the kill, and the
      // label to which side the victim was on. This used to announce every
      // death as a HOSTILE kill with a hitmarker — so shooting your own
      // teammate congratulated you for it.
      // Scoreboard bookkeeping, for bots as well as the player.
      e.deaths++;
      if (killer && killer !== this.player && killer.isEnemy) killer.kills++;
      // The distance is the award's whole point, so it is measured at the
      // moment of the kill rather than reconstructed later from two positions
      // that have both moved.
      this.mode?.stats?.onKill?.(killer ?? null, e,
        killer ? aimDistance(killer, e) : 0);
      const byPlayer = killer === this.player || (killer == null && this.lastShotByPlayer);
      const friendly = !this.player.team ? false : e.team === this.player.team;
      if (byPlayer) {
        this.hud.addKill(friendly ? 'TEAMMATE' : 'HOSTILE', this.weapon.spec.name, friendly);
        this.hud.hit(!friendly);
        this.audio.hitmarker(!friendly);
      }
      this.mode?.onEnemyDeath?.(e, byPlayer);
    };
    this.enemies.push(e);
    return e;
  }

  /**
   * Hold-to-plant / hold-to-defuse on the existing E/F verb.
   *
   * `Input.pressed` is a one-frame edge, so the hold uses `down()` and
   * accumulates. Progress resets the instant the key is released or the actor
   * moves more than `cancelMove` — that is what makes a fake defuse possible
   * and what gives the last second of a round its tension.
   *
   * Returns the HUD label for the current interaction, or null.
   */
  _updateBombInteraction(dt) {
    const bomb = this.bomb;
    if (!bomb || !this.player.alive) { this.progress = null; return null; }

    const held = this.input.down('KeyE') || this.input.down('KeyF');
    const pos = this.player.position;
    const team = this.player.team;

    // Online this is a PROMPT and nothing else.
    //
    // The server already runs plant and defuse — it reads the held key out of
    // every command and owns the timer, so a second one here would race it and
    // occasionally win, planting a bomb only this client believes in. The
    // progress bar comes from the snapshot too, via NetSession.decorateHud.
    if (this.net) {
      this.progress = null;
      const b = this.net.client.round?.bomb;
      if (!b) return null;
      if (team === 'red' && b.s === 'carried' && b.c === this.net.client.selfId) {
        return this._siteAt(pos) && this.player.grounded ? 'HOLD E TO PLANT' : null;
      }
      if (team === 'blue' && b.s === 'planted'
        && bomb.distanceTo(pos) <= BOMB_RULES.useRange) return 'HOLD E TO DEFUSE';
      return null;
    }

    // What, if anything, could we be doing here?
    let kind = null;
    if (team === 'red' && bomb.state === 'carried' && bomb.carrier === this.player) {
      const site = this._siteAt(pos);
      if (site && this.player.grounded) kind = { verb: 'PLANT', site };
    } else if (team === 'blue' && bomb.state === 'planted'
      && bomb.distanceTo(pos) <= BOMB_RULES.useRange) {
      kind = { verb: 'DEFUSE' };
    }

    if (!kind) { this.progress = null; return null; }
    if (!held) {
      this.progress = null;
      return `HOLD E TO ${kind.verb}`;
    }

    // Cancel on movement.
    if (this.progress && this.progress.from.distanceTo(pos) > BOMB_RULES.cancelMove) {
      this.progress = null;
      return `HOLD E TO ${kind.verb}`;
    }

    const total = kind.verb === 'PLANT'
      ? BOMB_RULES.plantTime
      : (BOMB_RULES.defuseKit ? BOMB_RULES.defuseTime : BOMB_RULES.defuseTimeNoKit);

    if (!this.progress || this.progress.verb !== kind.verb) {
      this.progress = { verb: kind.verb, t: 0, total, from: pos.clone() };
    }
    this.progress.t += dt;

    if (this.progress.t >= total) {
      if (kind.verb === 'PLANT') {
        _v.copy(pos);
        _v.y -= 1.6;                       // at the planter's feet
        bomb.plant(_v, kind.site.label);
        this.mode?.stats?.onPlant?.(this.player);
      } else {
        bomb.defuse();
        this.mode?.stats?.onDefuse?.(this.player);
      }
      this.progress = null;
      return null;
    }
    return kind.verb;
  }

  /** The bomb site containing `pos`, or null. */
  _siteAt(pos) {
    for (const s of this.level.bombSites ?? []) {
      if (s.box.containsPoint(pos)) return s;
    }
    // Also accept standing just above the box — the player's position is at eye
    // height, and a site box authored at floor level would otherwise never
    // contain them.
    _v.copy(pos);
    _v.y -= 1.6;
    for (const s of this.level.bombSites ?? []) {
      if (s.box.containsPoint(_v)) return s;
    }
    return null;
  }

  /** Who may pick up a dropped bomb standing at `pos`? Red actors only. */
  pickupCandidate(pos) {
    if (this.player.team === 'red' && this.player.alive
      && this.player.position.distanceTo(pos) < 1.6) return this.player;
    for (const e of this.enemies) {
      if (!e.alive || e.team !== 'red') continue;
      if (e.root.position.distanceTo(pos) < 1.4) return e;
    }
    return null;
  }

  /**
   * Nearest visible opponent for `me` — bot or player. Refreshed on a slow
   * timer rather than per frame; bots do not need frame-rate target churn.
   */
  _findTarget(me) {
    let best = null;
    let bestD = Infinity;

    const consider = (actor, pos) => {
      if (!actor || actor.alive === false) return;
      const d = me.root.position.distanceToSquared(pos);
      if (d < bestD) { bestD = d; best = actor; }
    };

    // A spawn-protected player is not a target either. Immunity alone would
    // still leave the whole map aiming at a defenceless spawn and opening fire
    // the instant it lapsed.
    if (me.isFoe(this.player) && !(this.player.spawnProtect > 0)) {
      consider(this.player, this.player.position);
    }
    for (const e of this.enemies) {
      if (e === me || !e.alive || !me.isFoe(e)) continue;
      consider(e, e.root.position);
    }
    return best;
  }

  /**
   * Who does this bot's round hit? Tests every actor on the opposing side.
   * Bots previously could not damage each other at all — only the player's
   * capsule was tested — so red-vs-blue combat was impossible.
   */
  _hitscanVictim(origin, dir, maxDist, shooter) {
    let best = null;
    let bestT = maxDist;

    for (const e of this.enemies) {
      if (e === shooter || !e.alive || !shooter.isFoe(e)) continue;
      // Cheap reject before touching the bone-driven hitboxes.
      _v.copy(e.root.position).sub(origin);
      const along = _v.dot(dir);
      if (along < 0 || along > bestT + 1.2) continue;
      if (_v.addScaledVector(dir, -along).length() > 1.6) continue;
      const h = e.raycastHit(origin, dir, bestT);
      if (h && h.distance < bestT) { bestT = h.distance; best = e; }
    }

    if (shooter.isFoe(this.player) && this.player.alive) {
      _v.copy(this.player.position).sub(origin);
      const proj = _v.dot(dir);
      if (proj > 0 && proj < bestT) {
        if (_v.addScaledVector(dir, -proj).length() < 0.45) best = this.player;
      }
    }
    return best;
  }

  /**
   * The player died. Deathmatch respawns after a beat; a round-based mode will
   * override this by setting `this.onPlayerDeath` to keep them dead.
   */
  _onPlayerDeath(dir) {
    if (this.bomb?.carrier === this.player) {
      _v.copy(this.player.position); _v.y -= 1.5;
      this.bomb.drop(_v);
    }
    this.progress = null;
    this.deaths++;
    this.respawnTimer = 3.0;
    this.mode?.stats?.onKill?.(this._lastAttacker ?? null, this.player,
      this._lastAttacker ? aimDistance(this._lastAttacker, this.player) : 0);
    this._lastAttacker = null;
    // Online the SERVER decides whether and when you come back, so the local
    // mode object is not consulted — in bomb mode it would claim the death and
    // suppress the banner, leaving a networked player killed with no feedback
    // at all.
    const handled = this.net ? false : this.mode?.onPlayerDeath?.(dir);
    if (!handled) this.hud.showObjective?.('YOU WERE KILLED', 'RESPAWNING', 2.6);
    this.audio.hitmarker?.(false);
    this.onPlayerDeath?.(dir);
  }

  /**
   * The Escape menu.
   *
   * DOM rather than canvas, deliberately: it has to take clicks while the game
   * is paused and the pointer is unlocked, and the menu screens are already
   * DOM, so it inherits their look for free.
   *
   * Leaving always goes through a full page load. Tearing a live match down in
   * place — sockets, rooms, bodies, physics, the render loop — is exactly the
   * kind of thing that leaves one listener behind and produces a bug nobody can
   * reproduce; a reload is honest and costs a second.
   */
  _showPauseMenu() {
    if (this._pauseEl) return;
    const online = !!this.net;
    const el = document.createElement('div');
    el.className = 'mapselect';
    el.style.zIndex = '60';
    const room = online ? this.net.client.roomInfo : null;
    const client = online ? this.net.client : null;
    const side = (t) => (t === 'red' ? 'ATTACKERS' : 'DEFENDERS');
    const myTeam = client?.team ?? null;
    const other = myTeam === 'red' ? 'blue' : 'red';
    const pending = client?.pendingTeam ?? null;
    // Bomb defusal has no mid-round respawn — that is the mode, not a fault —
    // so the menu says what actually happens next rather than leaving a dead
    // player wondering whether the game has hung.
    const roundBased = online && client?.serverMode === 'bomb';
    const dead = online && !this.player.alive;

    el.innerHTML = `
      <div class="ms-inner" style="width:min(560px,92vw)">
        <div class="ms-title">PAUSED</div>
        <div class="ms-sub">${online
          ? `${escapeHtml(room?.name ?? 'MULTIPLAYER')} · ${String(this.mapDef?.name ?? '').toUpperCase()}`
          + (myTeam ? ` · ${side(myTeam)}` : '')
          : `${String(this.mapDef?.name ?? '').toUpperCase()} · SINGLE PLAYER`}</div>
        ${dead ? `<div class="ms-blurb" style="margin-top:14px;color:#ffbe5a">
            YOU ARE DEAD — ${roundBased
    ? 'SPECTATING UNTIL THE NEXT ROUND'
    : 'RESPAWNING SHORTLY'}
          </div>` : ''}
        <div class="ms-cards" style="grid-template-columns:1fr;margin-top:26px">
          <button class="ms-card" data-act="resume">
            <span class="ms-name">RESUME</span>
            <span class="ms-meta">${dead && roundBased ? 'Back to spectating' : 'Back to the game'}</span>
            <span class="ms-go">CLICK OR PRESS ESC</span>
          </button>
          ${online && myTeam ? `
          <button class="ms-card" data-act="switch">
            <span class="ms-name">${pending ? 'CANCEL SIDE CHANGE' : `SWITCH TO ${side(other)}`}</span>
            <span class="ms-meta">${pending
    ? `You will join the ${side(pending)} next round — click to stay put`
    : 'Takes effect when the next round starts'}</span>
            <span class="ms-go">${pending ? 'STAY' : 'SWITCH'}</span>
          </button>` : ''}
          <button class="ms-card" data-act="settings">
            <span class="ms-name">GRAPHICS</span>
            <span class="ms-meta">Quality preset — currently ${escapeHtml(qualityById(this.quality).name)}</span>
            <span class="ms-go">CHANGE</span>
          </button>
          ${online ? '' : `
          <button class="ms-card" data-act="difficulty">
            <span class="ms-name">DIFFICULTY</span>
            <span class="ms-meta">Bot skill — currently ${escapeHtml(difficultyById(this.difficulty).name)}</span>
            <span class="ms-go">CHANGE</span>
          </button>`}
          <button class="ms-card" data-act="leave">
            <span class="ms-name">${online ? 'LEAVE SERVER' : 'CHANGE MAP OR MODE'}</span>
            <span class="ms-meta">${online ? 'Disconnect and return to the room list' : 'Back to map and mode selection'}</span>
            <span class="ms-go">${online ? 'DISCONNECT' : 'BACK'}</span>
          </button>
        </div>
        <div class="ms-status" role="status"></div>
      </div>`;
    document.getElementById('app').appendChild(el);
    this._pauseEl = el;
    // The canvas HUD draws its own "click to resume" hint while paused; two
    // overlapping prompts read as a bug.
    this.hidePrompt = true;

    el.addEventListener('click', (ev) => {
      const card = ev.target.closest('.ms-card');
      if (!card) return;
      if (card.dataset.act === 'resume') { this._hidePauseMenu(); this.input.requestLock(); return; }
      if (card.dataset.act === 'settings') {
        this._hidePauseMenu();
        this._showQualityMenu();
        return;
      }
      if (card.dataset.act === 'difficulty') {
        this._hidePauseMenu();
        this._showDifficultyMenu();
        return;
      }
      if (card.dataset.act === 'switch') {
        // Passing your CURRENT team is how the server reads "cancel".
        const want = pending ? myTeam : other;
        this.net.client.requestSwitch(want);
        el.querySelector('.ms-status').textContent = pending
          ? 'STAYING PUT'
          : `JOINING THE ${side(other)} NEXT ROUND`;
        // Repaint so the card reflects the new pending state.
        setTimeout(() => {
          if (!this._pauseEl) return;
          this._hidePauseMenu();
          this._showPauseMenu();
        }, 700);
        return;
      }
      el.querySelector('.ms-status').textContent = online ? 'DISCONNECTING…' : 'LOADING…';
      // Say goodbye properly so the server frees the slot and tells the rest of
      // the room, rather than waiting for a keepalive to notice.
      try { this.net?.client?.close(); } catch { /* already gone */ }
      // Drop every query parameter: `?map=` would otherwise drag the player
      // straight back into the game they just left.
      location.href = location.pathname;
    });

    // Escape again resumes, which is what every other game does.
    this._pauseKey = (e) => {
      if (e.code === 'Escape' && this._pauseEl) { this._hidePauseMenu(); this.input.requestLock(); }
    };
    addEventListener('keydown', this._pauseKey);
  }

  /**
   * The graphics presets.
   *
   * Applied on click and left on screen, so you can flick between them and
   * watch the frame counter and the picture change behind the menu — which is
   * the only way anybody can actually judge what a preset costs them. Nothing
   * here reloads: see src/render/Quality.js for why every pass is built
   * whatever the setting.
   */
  _showQualityMenu() {
    const el = document.createElement('div');
    el.className = 'mapselect';
    el.style.zIndex = '60';
    const paint = () => {
      el.innerHTML = `
        <div class="ms-inner" style="width:min(720px,94vw)">
          <div class="ms-title">GRAPHICS</div>
          <div class="ms-sub">APPLIES IMMEDIATELY · NO RESTART</div>
          <!-- Two columns, not one: five presets plus BACK in a single column
               pushed the status line and the way out below the fold. -->
          <div class="ms-cards" style="margin-top:22px">
            ${QUALITY_PRESETS.map((q) => `
              <button class="ms-card" data-q="${q.id}"
                ${q.id === this.quality ? 'style="border-color:#ffb020;background:rgba(255,176,32,.10)"' : ''}>
                <span class="ms-name">${q.name}${q.id === this.quality ? ' ·' : ''}</span>
                <span class="ms-meta">${escapeHtml(q.sub)}</span>
                <span class="ms-blurb">${escapeHtml(q.blurb)}</span>
                <span class="ms-go">${q.id === this.quality ? 'ACTIVE' : 'SELECT'}</span>
              </button>`).join('')}
            <button class="ms-card" data-q="back">
              <span class="ms-name">BACK</span>
              <span class="ms-meta">Return to the pause menu</span>
              <span class="ms-go">RETURN</span>
            </button>
          </div>
          <div class="ms-status" role="status">RENDER SCALE ${Math.round(this.engine.renderScale * 100)}%
            · ${Math.round(this._fps ?? 0)} FPS</div>
        </div>`;
    };
    paint();
    document.getElementById('app').appendChild(el);
    this._pauseEl = el;
    this.hidePrompt = true;

    el.addEventListener('click', (ev) => {
      const card = ev.target.closest('.ms-card');
      if (!card) return;
      const id = card.dataset.q;
      if (id === 'back') { this._hidePauseMenu(); this._showPauseMenu(); return; }
      this.setQuality(id);
      // Repainted after a beat so the FPS readout has caught up with the change
      // rather than showing the number from before it.
      setTimeout(() => { if (this._pauseEl === el) paint(); }, 600);
      paint();
    });

    this._pauseKey = (e) => {
      if (e.code === 'Escape' && this._pauseEl === el) {
        this._hidePauseMenu();
        this.input.requestLock();
      }
    };
    addEventListener('keydown', this._pauseKey);
  }

  /**
   * Bot difficulty.
   *
   * Same shape as the graphics menu and for the same reason: applied on click
   * and left on screen, so the change lands on the bots already in the level
   * and you can go straight back and feel it. Nothing restarts — see
   * `Difficulty.applySkill` for why re-stamping a live bot is safe.
   */
  _showDifficultyMenu() {
    const el = document.createElement('div');
    el.className = 'mapselect';
    el.style.zIndex = '60';
    const paint = () => {
      el.innerHTML = `
        <div class="ms-inner" style="width:min(720px,94vw)">
          <div class="ms-title">DIFFICULTY</div>
          <div class="ms-sub">APPLIES IMMEDIATELY · AFFECTS THE BOTS ALREADY IN THE LEVEL</div>
          <div class="ms-cards" style="margin-top:22px">
            ${DIFFICULTIES.map((d) => `
              <button class="ms-card" data-d="${d.id}"
                ${d.id === this.difficulty ? 'style="border-color:#ffb020;background:rgba(255,176,32,.10)"' : ''}>
                <span class="ms-name">${d.name}${d.id === this.difficulty ? ' ·' : ''}</span>
                <span class="ms-meta">${escapeHtml(d.sub)}</span>
                <span class="ms-blurb">${escapeHtml(d.blurb)}</span>
                <span class="ms-go">${d.id === this.difficulty ? 'ACTIVE' : 'SELECT'}</span>
              </button>`).join('')}
            <button class="ms-card" data-d="back">
              <span class="ms-name">BACK</span>
              <span class="ms-meta">Return to the pause menu</span>
              <span class="ms-go">RETURN</span>
            </button>
          </div>
          <div class="ms-status" role="status">${this.enemies.filter((e) => e.alive).length} BOTS IN THE LEVEL</div>
        </div>`;
    };
    paint();
    document.getElementById('app').appendChild(el);
    this._pauseEl = el;
    this.hidePrompt = true;

    el.addEventListener('click', (ev) => {
      const card = ev.target.closest('.ms-card');
      if (!card) return;
      const id = card.dataset.d;
      if (id === 'back') { this._hidePauseMenu(); this._showPauseMenu(); return; }
      this.setDifficulty(id);
      paint();
    });

    this._pauseKey = (e) => {
      if (e.code === 'Escape' && this._pauseEl === el) {
        this._hidePauseMenu();
        this.input.requestLock();
      }
    };
    addEventListener('keydown', this._pauseKey);
  }

  /**
   * Change the bot difficulty, remember it, and re-stamp the bots that already
   * exist — a setting that only took effect on the next spawn would look like
   * it had not worked at all.
   *
   * Ignored online: the server owns the bots there.
   */
  setDifficulty(id) {
    if (this._netClient) return difficultyById(this.difficulty);
    const d = difficultyById(id);
    this.difficulty = d.id;
    saveDifficulty(d.id);
    for (const e of this.enemies) applySkill(e, d.id);
    return d;
  }

  _hidePauseMenu() {
    if (this._pauseEl) this.hidePrompt = false;
    this._pauseEl?.remove();
    this._pauseEl = null;
    if (this._pauseKey) { removeEventListener('keydown', this._pauseKey); this._pauseKey = null; }
  }

  /**
   * Change the graphics preset and remember it.
   *
   * The menu's own path, exposed so a probe drives exactly what a player does
   * rather than reaching into `applyQuality` with its own idea of the arguments.
   */
  setQuality(id) {
    const q = applyQuality(this, id);
    saveQuality(q.id);
    return q;
  }

  /** Put the player back at a spawn point, facing the middle of the map. */
  respawnPlayer() {
    this.spectator.reset();
    // Away from whoever is still alive — respawning inside the bot pack and
    // dying again before the screen had finished fading was the single most
    // common way to lose a deathmatch life.
    const p = this._pickSpawn(this.player);
    // Face roughly toward the map centre so you never respawn staring at a wall.
    const yaw = Math.atan2(-(0 - p.x), -(0 - p.z));
    this.player.respawn(p, yaw);
    this.weapon.ammo = this.weapon.spec.mag;
    this.postfx.setDamage(0);
  }

  _onPlayerHit(fromPos, shooter = null, amount = 0) {
    _v.copy(fromPos).sub(this.player.position);
    const worldAngle = Math.atan2(_v.x, _v.z);
    this.hud.damageFrom(worldAngle - this.player.yaw + Math.PI);
    // Remembered so a death can be credited to somebody. The player's
    // controller does not record who shot it, and by the time onDeath fires the
    // only thing that knew is this call.
    if (shooter) {
      this._lastAttacker = shooter;
      this.mode?.stats?.onDamage?.(shooter, this.player, amount, 'body');
    }
  }

  fire() {
    const cam = this.engine.camera;
    const spec = this.weapon.spec;
    if (!this.weapon.tryFire(performance.now() * 0.001, this.effects)) return;
    // Taking a shot gives up spawn protection, so it can shelter a retreat but
    // never win a trade.
    this.player.endSpawnProtection();
    // Accuracy, offline. Counted where the weapon actually cycled, so a click
    // on an empty magazine is not a miss.
    this.mode?.stats?.onShot?.(this.player);
    // Online, whether this shot hit anything is the server's decision. The
    // muzzle flash, sound and tracer still play locally and immediately —
    // waiting a round trip to see your own gun fire feels broken.
    if (this.net) this.net.fire(this.weaponKeyFor(this.weapon.spec));
    this.weapon.lastFire = performance.now() * 0.001;

    cam.getWorldDirection(_dir);
    const origin = cam.position.clone();
    this.weapon.muzzleWorld(_muzzle);

    // Melee has no muzzle, no shell and no report — handle it before any of
    // that fires.
    if (spec.melee) { this._melee(spec); return; }

    // Shared muzzle presentation, whatever the projectile type.
    this.effects.muzzleFlash(
      this.weapon.muzzle.getWorldPosition(new THREE.Vector3()), _muzzle,
      spec.flashScale ?? 1
    );
    if (spec.ejects !== false) {
      this.effects.ejectShell(_muzzle, cam.quaternion, this.player.velocity);
    }
    this.audio.gunshot(_muzzle, {
      close: true, gain: spec.report ?? 1, voice: voiceFor(spec),
    });

    // Launchers throw a physical projectile instead of a hitscan ray.
    if (spec.projectile) {
      this.spawnRocket(_muzzle.clone(), _dir.clone(), spec);
      return;
    }

    // Hitscan. Shotguns throw a pattern; everything else a single round.
    //
    // The pattern is DETERMINISTIC and comes from the shared ballistics module,
    // because the server fires the same nine rounds from the same function. It
    // used to be nine random directions here against a single ray there, so the
    // spread you watched had no relationship to the damage you did.
    const pellets = Math.max(1, spec.pellets ?? 1);
    if (pellets > 1) {
      const dirs = [];
      pelletPattern(_dir, spec.pelletCone ?? 0.04, pellets,
        (x, y, z) => dirs.push(new THREE.Vector3(x, y, z)));
      for (const d of dirs) this._hitscan(origin, d, spec, true);
    } else {
      this._hitscan(origin, _dir, spec, false);
    }
  }

  /**
   * Melee swing. A short trace rather than a hitscan round: a knife has no
   * spread and no falloff, it either reaches or it does not. Widened slightly
   * against enemies so a swing that visually connects actually lands.
   */
  _melee(spec) {
    const cam = this.engine.camera;
    cam.getWorldDirection(_dir);
    const origin = cam.position.clone();
    const reach = spec.range;

    this.audio.melee?.(origin);
    this.weapon.swing?.();

    const worldHit = this.physics.raycast(origin, _dir, reach);
    let closest = worldHit ? worldHit.distance : reach;
    let target = null, info = null;
    for (const e of this.actors) {
      const h = e.raycastHit(origin, _dir, reach);
      if (h && h.distance <= closest + 0.35) { closest = h.distance; target = e; info = h; }
    }

    if (target) {
      // A knife from behind is lethal; from the front it is a heavy hit.
      _v.copy(target.root.position).sub(origin).setY(0).normalize();
      const facing = new THREE.Vector3(-Math.sin(target.yaw), 0, -Math.cos(target.yaw));
      const fromBehind = facing.dot(_v) > 0.35;
      // A knife has no falloff — it either reaches or it does not — but a hit
      // location still prices it.
      const dmg = fromBehind ? 500 : spec.damage * zoneMultiplier(spec, info.part);
      // Name the attacker. Without it the victim falls back to whoever it was
      // already fighting, so a bot the player shot never turned around — it
      // took the damage and kept duelling another bot. That reads in game as
      // the enemy team refusing to resist you.
      // Same split as a bullet: the swing connects visibly and audibly at once,
      // but online the server decides what a knife in the back is worth.
      const killed = this.net ? false : target.damage(dmg, info.part, _dir, this.player);
      this.effects.impact(info.point, _dir.clone().negate(), 'flesh');
      this.audio.meleeHit?.(info.point);
      if (!this.net && !killed) {
        this.hud.hit(info.part === 'head' ? 'head' : 'body');
        this.audio.hitmarker(false);
      }
    } else if (worldHit && worldHit.distance < reach) {
      this.effects.impact(worldHit.point, worldHit.normal, 'concrete');
      this.audio.impact(worldHit.point, 'concrete');
    }
  }

  /** One hitscan round with the current cone applied. */
  _hitscan(origin, aim, spec, isPellet) {
    _v.copy(aim);
    // A pellet's direction is already the pattern; only single rounds take the
    // weapon's current spread, which is what ADS and recoil move.
    if (!isPellet) {
      const spread = this.weapon.spreadNow;
      _v.x += (Math.random() - 0.5) * spread * 2;
      _v.y += (Math.random() - 0.5) * spread * 2;
      _v.z += (Math.random() - 0.5) * spread * 2;
    }
    _v.normalize();

    const range = spec.range;
    const worldHit = this.physics.raycast(origin, _v, range);
    let closest = worldHit ? worldHit.distance : range;
    let hitEnemy = null;
    let hitInfo = null;

    // Remote players count as something the round can hit. Without this the
    // tracer flew PAST an online opponent and burst on the wall behind them,
    // and you got a concrete puff where the blood should have been.
    for (const e of this.actors) {
      const h = e.raycastHit(origin, _v, closest);
      if (h && h.distance < closest) { closest = h.distance; hitEnemy = e; hitInfo = h; }
    }

    // Tracer from the muzzle so it reads correctly. Shotgun pellets only
    // tracer occasionally, or the pattern reads as a solid cone of light.
    if (!isPellet || Math.random() < 0.25) {
      const end = origin.clone().addScaledVector(_v, closest);
      this.effects.tracer(_muzzle, end, 460);
    }

    if (hitEnemy) {
      // Range and hit location, from the same table the server uses. Every
      // weapon used to do one flat number at any distance.
      const dmg = damageAtRange(spec, closest);
      const mult = zoneMultiplier(spec, hitInfo.part);
      // The blood plays either way — that is local presentation, and it should
      // not wait a round trip. The DAMAGE and the hitmarker do not: online the
      // server resolves this same shot and sends both back, so applying them
      // here would be a guess that sometimes contradicts it and a marker that
      // appears twice.
      const killed = this.net ? false : hitEnemy.damage(dmg * mult, hitInfo.part, _v, this.player);
      this.effects.impact(hitInfo.point, _v.clone().negate(), 'flesh');
      if (!this.net && !killed) {
        this.hud.hit(hitInfo.part === 'head' ? 'head' : 'body');
        this.audio.hitmarker(false);
      }
    } else if (worldHit) {
      this.effects.impact(worldHit.point, worldHit.normal, 'concrete');
      if (!isPellet || Math.random() < 0.4) this.audio.impact(worldHit.point, 'concrete');
    }
  }

  /** Rocket: a travelling body that detonates on contact with splash damage. */
  spawnRocket(pos, dir, spec) {
    const p = spec.projectile;
    // Pooled: building a fresh MeshStandardMaterial per shot compiles a shader
    // program mid-combat, which is a visible hitch every time you pull the
    // trigger. One material, meshes reused.
    const P = this._rocketPool ??= {
      geo: new THREE.CapsuleGeometry(0.055, 0.16, 4, 10),
      mat: new THREE.MeshStandardMaterial({ color: 0x3c4038, roughness: 0.6, metalness: 0.4 }),
      free: [],
    };
    const mesh = P.free.pop() ?? (() => {
      const m = new THREE.Mesh(P.geo, P.mat);
      this.engine.scene.add(m);
      return m;
    })();
    mesh.visible = true;
    mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    mesh.position.copy(pos);

    this.rockets.push({
      mesh,
      owner: this.player,          // so splash damage names its attacker
      pos: pos.clone(),
      dir: dir.clone(),
      speed: p.speed ?? 34,
      life: p.life ?? 6,
      // Blast from the shared table, not the weapon's own copy. The server
      // adjudicates online explosions from EXPLOSIVES, so a second set of
      // numbers here would mean the blast you watch and the blast that hurts
      // people are different sizes.
      radius: EXPLOSIVES.rpg.radius,
      damage: EXPLOSIVES.rpg.damage,
      trail: 0,
    });
  }

  updateRockets(dt) {
    for (let i = this.rockets.length - 1; i >= 0; i--) {
      const r = this.rockets[i];
      r.life -= dt;
      const stepLen = r.speed * dt;

      // Sweep the segment so a fast rocket cannot tunnel through a wall.
      const hit = this.physics.raycast(r.pos, r.dir, stepLen);
      let detonateAt = null;
      if (hit) detonateAt = hit.point.clone();

      let closest = hit ? hit.distance : stepLen;
      // Bots offline, remote players online. Reading `enemies` here meant a
      // rocket passed straight through every player in a network game and flew
      // on until it met a wall — so the blast, however well the server then
      // adjudicated it, went off somewhere behind them.
      for (const e of this.actors) {
        const h = e.raycastHit(r.pos, r.dir, closest);
        if (h && h.distance < closest) { closest = h.distance; detonateAt = h.point.clone(); }
      }

      r.pos.addScaledVector(r.dir, stepLen);
      r.mesh.position.copy(r.pos);

      // Exhaust trail.
      r.trail += dt;
      if (r.trail > 0.02) {
        r.trail = 0;
        this.effects.puff(r.pos, { color: 0x6e6a63, size0: 0.10, size1: 0.7, ttl: 0.9, opacity: 0.35 });
      }

      if (detonateAt || r.life <= 0) {
        // Only the PLAYER's rocket is reported: a bot's exists on this machine
        // alone, and online there are no bots.
        this.detonate(detonateAt ?? r.pos.clone(), r.radius, r.damage, r.owner ?? null,
          r.owner === this.player ? 'rpg' : null);
        r.mesh.visible = false;
        this._rocketPool.free.push(r.mesh);   // reuse, don't dispose
        this.rockets.splice(i, 1);
      }
    }
  }

  /**
   * Throw a grenade. Uses the existing sphere-body physics so it arcs, bounces
   * off walls and rolls, rather than flying a scripted path.
   */
  throwGrenade(kind) {
    const slot = this.equipment[kind];
    if (!slot || slot.n <= 0 || this.weapon.reloading) return;
    slot.n--;

    const cam = this.engine.camera;
    cam.getWorldDirection(_dir);
    const start = cam.position.clone().addScaledVector(_dir, 0.4);
    start.y -= 0.12;

    // Pooled per kind, for the same reason as rockets.
    const pools = this._nadePool ??= {
      frag: { geo: new THREE.SphereGeometry(0.045, 12, 10),
        mat: new THREE.MeshStandardMaterial({ color: 0x37402f, roughness: 0.68, metalness: 0.25 }), free: [] },
      flash: { geo: new THREE.CylinderGeometry(0.033, 0.033, 0.115, 10),
        mat: new THREE.MeshStandardMaterial({ color: 0x6d6f72, roughness: 0.68, metalness: 0.25 }), free: [] },
    };
    const P = pools[kind];
    const mesh = P.free.pop() ?? (() => {
      const m = new THREE.Mesh(P.geo, P.mat);
      m.castShadow = true;
      this.engine.scene.add(m);
      return m;
    })();
    mesh.visible = true;
    mesh.position.copy(start);

    // Inherit the player's motion — throwing while sprinting should carry.
    const vel = _dir.clone().multiplyScalar(this.throwSpeed)
      .addScaledVector(this.player.velocity, 0.85);
    // A little loft so a level throw arcs instead of dropping flat. Kept small
    // deliberately: a large constant lifts every throw the same amount and
    // drowns out the aim, and where the grenade lands should be something the
    // player aims, not something the code decides.
    vel.y += this.throwLoft;

    const body = this.physics.addBody({
      position: mesh.position,
      quaternion: mesh.quaternion,
      velocity: vel,
      radius: 0.05,
      drag: 0.25,
      restitution: 0.34,
      life: 30,
      spin: { axis: new THREE.Vector3(Math.random(), Math.random(), Math.random()).normalize(), rate: 11 },
      onBounce: (n, speed) => { if (speed > 1.2) this.audio.shellDrop(mesh.position, speed * 0.5); },
      onExpire: () => {},
    });

    this.throwables.push({
      kind, mesh, body,
      owner: this.player,

      fuse: kind === 'frag' ? 2.6 : 1.7,
    });
  }

  updateThrowables(dt) {
    for (let i = this.throwables.length - 1; i >= 0; i--) {
      const t = this.throwables[i];
      t.fuse -= dt;
      if (t.fuse > 0) continue;

      const pos = t.mesh.position.clone();
      if (t.kind === 'frag') {
        // 6 m was a lethal radius you had to land on top of someone to use.
        // 9.5 m is a frag's realistic casualty radius and, with the linear
        // falloff and the line-of-sight test in detonate(), still rewards
        // placement — cover behind a wall remains complete protection.
        this.detonate(pos, EXPLOSIVES.frag.radius, EXPLOSIVES.frag.damage, t.owner ?? null,
          t.owner === this.player ? 'frag' : null);
      } else {
        this.flashbang(pos);
      }
      this.physics.removeBody(t.body);
      t.mesh.visible = false;
      this._nadePool[t.kind].free.push(t.mesh);   // reuse, don't dispose
      this.throwables.splice(i, 1);
    }
  }

  /**
   * Flashbang: a hard white pop plus a lingering screen wash, and enemies with
   * line of sight to it are stunned — their reaction delay is reset and they
   * lose track of the player, rather than simply taking damage.
   */
  /**
   * @param report  send it to the server, which decides who it blinds. False
   *   when the bang is one the server has ALREADY adjudicated and is telling us
   *   about, or reporting it would bounce back and forth forever.
   */
  flashbang(pos, report = true) {
    this.effects.spark(pos, new THREE.Vector3(0, 1, 0), 30, 1.6, 2.0);
    this.effects.puff(pos, { color: 0xffffff, size0: 0.4, size1: 2.4, ttl: 0.9, opacity: 0.5 });
    this.audio.gunshot(pos, { close: false, gain: 2.0, voice: 'shotgun' });

    // Online, being blinded is not this machine's call — not even about itself.
    // It depends on where each player was looking, and the server is the only
    // thing that knows that about everyone. The pop above still plays at once,
    // because a bang you see late is a bang that did not happen.
    if (this.net) {
      if (report) this.net.flash(pos);
      return;
    }

    const eye = this.engine.camera.position;
    const d = eye.distanceTo(pos);
    if (d < 22) {
      _v.copy(pos).sub(eye);
      const dist = _v.length();
      _v.divideScalar(dist || 1);
      // Facing matters: a bang behind you is far less effective.
      this.engine.camera.getWorldDirection(_dir);
      const facing = THREE.MathUtils.clamp(_dir.dot(_v), -1, 1);
      const blocked = this.physics.raycast(eye, _v, dist - 0.3);
      if (!blocked) {
        const k = (1 - d / 22) * (0.35 + 0.65 * Math.max(0, facing));
        this.flash = Math.max(this.flash ?? 0, k);
      }
    }

    for (const e of this.enemies) {
      if (!e.alive) continue;
      const ed = e.root.position.distanceTo(pos);
      if (ed > 18) continue;
      _v.copy(e.eyePosition).sub(pos);
      const dist = _v.length();
      _v.divideScalar(dist || 1);
      if (this.physics.raycast(pos, _v, dist - 0.3)) continue;
      const k = 1 - ed / 18;
      e.reaction = Math.max(e.reaction ?? 0, 0.6 + 2.6 * k);
      e.alert = Math.max(0, (e.alert ?? 0) - k);
      e.setState?.('search');
    }
  }

  /**
   * @param kind  which entry in EXPLOSIVES this is, for the server. Omitted for
   *   blasts the server has no way to authorise — see below.
   */
  detonate(pos, radius, damage, attacker = null, kind = null) {
    this.effects.explosion(pos, radius);
    this.audio.gunshot(pos, { close: false, gain: 1.6, voice: 'launcher' });

    // Online, who this hurt is not this machine's to decide.
    //
    // The flight IS ours — we simulated the rocket and bounced the grenade, and
    // waiting a round trip to watch your own grenade go off would feel broken —
    // so the visuals above play immediately and the server is told WHERE it
    // happened. It decides the rest and tells everyone, including us.
    //
    // A blast with no `kind` is one the server cannot authorise: killstreaks
    // are earned from a kill count this client keeps for itself, so a request
    // to drop nine 220-damage bombs at coordinates of the client's choosing is
    // not something the server can distinguish from a cheat. Those stay
    // cosmetic online until streaks are server-owned.
    if (this.net) {
      if (kind) this.net.explode(kind, pos);
      return;
    }

    for (const e of this.actors) {
      if (!e.alive) continue;
      const d = e.root.position.distanceTo(pos);
      if (d > radius) continue;
      // Linear falloff, and require line of sight so walls actually protect.
      _v.copy(e.root.position).setY(e.root.position.y + 0.9).sub(pos);
      const dist = _v.length();
      _v.divideScalar(dist || 1);
      const blocked = this.physics.raycast(pos, _v, dist - 0.3);
      if (blocked) continue;
      const falloff = 1 - d / radius;
      const killed = e.damage(damage * falloff, 'body', _v, attacker ?? null);
      if (killed) this.hud.hit('kill'); else this.hud.hit('body');
    }

    // The player is not immune to their own rocket.
    const pd = this.player.position.distanceTo(pos);
    if (pd < radius) {
      this.player.damage(damage * (1 - pd / radius) * 0.6);
      this.player.addTrauma(0.9 * (1 - pd / radius));
      this._onPlayerHit(pos);
    }
  }

  update(dt) {
    const input = this.input;
    this.clockNow += dt;

    // Streaks tick regardless of whether the player is alive. An airstrike you
    // called is in the air whether or not it just killed you — freezing the run
    // on death left half of it hanging permanently.
    this.streaks.update(dt);

    // ---- dead ------------------------------------------------------------
    // Everything the player drives is skipped, but the world keeps simulating:
    // bots, physics and effects all tick, so you can watch the fight continue.
    if (!this.player.alive) {
      this.player.update(dt, {});          // look + death camera only
      for (const e of this.enemies) e.update(dt);
      this.enemies = this.enemies.filter((e) => !e._removed);
      this.physics.step(dt);
      this.effects.update(dt);
      this.updateRockets(dt);
      this.updateThrowables(dt);
      this.level.update?.(dt);
      // The fuse keeps burning while you are dead — watching it run out is
      // most of what spectating a lost round feels like. Online it is the
      // server's fuse, same as when alive.
      if (this.net) this.bomb?.syncNet(this.net.client.round?.bomb, dt);
      else this.bomb?.update(dt);
      // NOT online. The local mode object exists so the HUD and offline game
      // keep one shape, but ticking it in a network game runs a SECOND round
      // machine on this machine — and its round transitions call spawnEnemy(),
      // which is how bots kept appearing in multiplayer after mode.start() was
      // already suppressed.
      if (!this.net) this.mode?.update(dt);
      // Online, the session has to keep running while you are dead or nothing
      // arrives: no snapshots consumed, no bodies moved, and — the part that
      // strands you — no way to hear that the server has respawned you. The
      // command it sends is ignored server-side for a dead player, which is
      // exactly right; what matters is that the socket keeps being read.
      if (this.net) this.net.update(dt, input, { passive: true });
      // Follow a living teammate rather than your own corpse. Runs after the
      // controller has written the camera, so the death collapse still plays.
      this.spectator.update(dt, input);
      // The post stack's damage grade is set from health in the ALIVE path,
      // which this branch returns before reaching — so on death it froze at
      // full strength and stayed there. Three seconds of that is dying; a whole
      // bomb round of it is watching your team through a red filter. It fades
      // once the camera has left the corpse and you are a spectator rather
      // than a casualty.
      this.postfx.setDamage(this.spectator.active ? 0 : 1);
      this.atmosphere.update(this.player.position, dt);
      this.audio.update(this.engine.camera);

      // In a round-based mode `respawnTimer` is Infinity — the mode respawns
      // everyone itself at the start of the next round. Online it is the
      // SERVER's call, and respawning locally would put you somewhere it has
      // never heard of until the next snapshot yanked you back.
      if (!this.net) {
        this.respawnTimer -= dt;
        if (this.respawnTimer <= 0) this.respawnPlayer();
      }
      return this.weapon.ads;
    }

    // Alive: make sure spectating is off. `Spectator.update` only runs in the
    // dead branch, so a respawn that does not go through `respawnPlayer` — the
    // round restart calls `player.respawn` directly — would otherwise leave the
    // chase camera latched on.
    if (this.spectator.active) this.spectator.reset();

    if (input.pressed('KeyR')) this.weapon.reload();
    // Use: buttons open the bunker and call the lifts.
    if (input.pressed('KeyE') || input.pressed('KeyF')) {
      if (this.net) {
        /*
         * Online, the SERVER presses the button.
         *
         * Not both: `trigger` is a toggle, so firing locally and then again on
         * the server's echo would send the lift straight back where it came
         * from. The ask goes up, the server decides, and the answer comes back
         * as an event every client applies — including this one — so all of
         * them start the same lift at the same instant.
         */
        const btn = this.level.nearestButton?.(this.player.position);
        if (btn) this.net.pressButton();
      } else {
        const fired = this.level.use?.(this.player.position);
        if (fired) this.audio.reload('bolt');
      }
    }
    this.level.update?.(dt);
    if (this.bomb) {
      // Online the bomb is a puppet: the server owns its state, and this
      // machine only draws and beeps it. Running its own update would plant,
      // drop and detonate a second bomb nobody else can see.
      if (this.net) this.bomb.syncNet(this.net.client.round?.bomb, dt);
      else this.bomb.update(dt);
      this.bombPrompt = (this.net ? this.net.client.inputLocked : this.mode.inputLocked)
        ? null : this._updateBombInteraction(dt);
    }
    // Slots are driven off the WEAPONS table, so adding a weapon there gives it
    // a number key without touching this.
    for (let i = 0; i < Math.min(9, this.slots.length); i++) {
      if (input.pressed(`Digit${i + 1}`)) this.switchWeapon(this.slots[i]);
    }

    // Killstreaks: one key spends the highest tier you hold.
    if (input.pressed('KeyF')) {
      const used = this.streaks.use();
      if (!used) this.audio?.dryFire?.();
    }
    this.streaks.syncKills(this.hud.kills);

    // Throwables.
    if (input.pressed('KeyG')) this.throwGrenade('frag');
    if (input.pressed('KeyQ')) this.throwGrenade('flash');
    this.updateThrowables(dt);
    if (input.mouse.wheel) {
      const cur = this.slots.indexOf(this.weapon.spec);
      const next = (cur + (input.mouse.wheel > 0 ? 1 : -1) + this.slots.length) % this.slots.length;
      this.switchWeapon(this.slots[next]);
    }

    const playerOpts = {
      ads: this.weapon.ads > 0.5,
      aimSensitivityScale: 1 - this.weapon.ads * 0.45,
      // Freeze time and round end: you can look around but not move.
      //
      // Online this is the SERVER's call, not ours. The local mode object is
      // never started in a network game, so its inputLocked is whatever it
      // happened to initialise to — and in bomb mode that is "frozen", which
      // silently pinned the player in place for the whole match.
      // The server decides when movement is locked; the client must apply the
      // same rule or prediction fights it (measured: a constant 147 mm error).
      frozen: this.net ? this.net.client.inputLocked : this.mode.inputLocked,
    };
    if (this.net) {
      // Online the local player is driven by a COMMAND, not straight from the
      // keyboard: the same command is sent to the server and kept for replay.
      this.net.update(dt, this.input, playerOpts);
    } else {
      this.player.update(dt, playerOpts);
    }
    const adsEase = this.weapon.update(dt, input);

    const shouldFire = (this.weapon.spec.mode === 'auto' ? input.mouse.left : input.mouse.leftEdge)
      && (this.net ? !this.net.client.inputLocked : !this.mode.inputLocked);
    if (shouldFire) this.fire();
    this.updateRockets(dt);

    // Footsteps driven by the same bob phase as the camera so they land in sync.
    const hSpeed = Math.hypot(this.player.velocity.x, this.player.velocity.z);
    if (this.player.grounded && hSpeed > 1.2) {
      this.footstepAccum += dt * (hSpeed / 2.6);
      if (this.footstepAccum >= 1) {
        this.footstepAccum = 0;
        this.audio.footstep(this.player.position, this.player.sprinting ? 1 : 0.6);
      }
    }

    /*
     * Contact detection for the minimap, on the same slow cadence as retargeting.
     * A bot is a known contact while it is shooting at you, or while you have
     * eyes on it — and it stays known for a few seconds after, so a blip does
     * not strobe every time someone steps behind a crate.
     */
    this._detectTimer = (this._detectTimer ?? 0) - dt;
    if (this._detectTimer <= 0) {
      this._detectTimer = 0.2;
      const cam = this.engine.camera;
      cam.getWorldDirection(_dir);
      // Works for remote players too: the line-of-sight half of this needs
      // nothing but a position, so online contacts come out of the same rule
      // that governs bots. Only the "is it shooting at me" branch is
      // bot-specific — online that is stamped from the server's hit events,
      // which is the one thing this client cannot see for itself.
      for (const e of this.actors) {
        if (!e.alive) continue;
        if (e.state === 'engage' && e.target === this.player) {
          e._knownUntil = this.clockNow + 4;
          continue;
        }
        _v.copy(e.root.position).setY(e.root.position.y + 1.2).sub(cam.position);
        const dist = _v.length();
        if (dist > 80) continue;
        _v.divideScalar(dist);
        if (_v.dot(_dir) < 0.30) continue;                 // outside your view
        if (this.physics.raycast(cam.position, _v, dist - 0.6)) continue;
        e._knownUntil = this.clockNow + 3;
      }
    }

    // Retarget every 0.4 s. Per-frame target churn is wasted work and makes
    // bots visibly twitch between opponents.
    this._retargetTimer = (this._retargetTimer ?? 0) - dt;
    if (this._retargetTimer <= 0) {
      this._retargetTimer = 0.4;
      for (const e of this.enemies) {
        if (e.alive) e.target = this._findTarget(e);
      }
    }
    for (const e of this.enemies) e.update(dt);
    this._updateAimTarget();
    // Retire enemies whose corpses have despawned.
    this.enemies = this.enemies.filter((e) => !e._removed);

    this.physics.step(dt);
    this.effects.update(dt);
    if (!this.net) this.mode?.update(dt);
    this.atmosphere.update(this.player.position, dt);
    this.audio.update(this.engine.camera);

    // Viewmodel camera is pinned to identity; the viewmodel group carries pose.
    this.engine.viewmodelCamera.position.set(0, 0, 0);
    this.engine.viewmodelCamera.quaternion.identity();

    const hpFrac = 1 - this.player.health / this.player.maxHealth;
    this.postfx.setDamage(Math.min(1, hpFrac * 1.35));
    // Flashbang wash: snaps to full then bleeds off over a couple of seconds.
    if (this.flash > 0) {
      this.flash = Math.max(0, this.flash - dt * 0.55);
      this.postfx.setFlash(this.flash * this.flash * 2.2);
    } else {
      this.postfx.setFlash(0);
    }
    // Atmosphere owns the absolute exposure; ADS only applies a relative trim.
    // This used to assign an absolute lerp(1.0, 0.92), which silently clobbered
    // the atmosphere's calibrated value every frame and made gameplay ~1.8x
    // brighter than any paused capture the artists were grading against.
    const baseExposure = this.atmosphere.exposure ?? 1.0;
    this.engine.renderer.toneMappingExposure = baseExposure * THREE.MathUtils.lerp(1.0, 0.92, adsEase);

    return adsEase;
  }

  switchWeapon(spec) {
    if (this.weapon.spec === spec) return;
    this.engine.viewmodelScene.remove(this.weapon.group);
    const reserve = this.weapon.reserve;
    this.weapon = new Weapon(this.engine, this.player, spec);
    this.weapon.onReloadStart = () => this.audio.reload('out');
  }

  /**
   * Build the HUD state object. The HUD reads every field defensively and has
   * fallbacks that reach through `window.__game`, but that is a debug hook and
   * shouldn't be load-bearing in the shipped build — so supply them properly.
   * The arrays are reused across frames to keep the loop allocation-free.
   */
  /**
   * Frame rate, smoothed.
   *
   * Instantaneous 1/dt jitters by tens of frames between vsync intervals and is
   * unreadable; an exponential average settles enough to be worth putting on
   * screen while still reacting within a second to a real drop.
   */
  _updateFps(dt) {
    if (dt > 0) {
      const inst = 1 / dt;
      this._fps = this._fps ? this._fps + (inst - this._fps) * 0.08 : inst;
    }
    return this._fps ?? 0;
  }

  _hudState(adsEase) {
    const s = this._hudStateObj ??= { enemies: [], pos: new THREE.Vector3() };

    s.pos.copy(this.player.position);
    // Real map and mode names. These were hard-coded to AL-RASHEED / TEAM
    // DEATHMATCH, so every map claimed to be the first one.
    s.mapName = this.mapDef?.name ?? 'AL-RASHEED';
    // Bomb sites for the minimap. Only meaningful in a mode that has them.
    s.sites = this.modeId === 'bomb' && this.level.bombSites?.length
      ? (this._siteBlips ??= this.level.bombSites.map((b) => ({
        label: b.label, x: b.center.x, z: b.center.z,
      })))
      : null;
    s.mapSub = this.mapDef?.sub ?? '';
    s.modeName = this.mode?.constructor?.id === 'bomb' ? 'BOMB DEFUSAL' : 'TEAM DEATHMATCH';

    // Minimap blips. `hot` marks an enemy that currently has eyes on the player.
    /*
     * Minimap contacts.
     *
     * These used to be every enemy on the map, every frame — a permanent
     * wallhack, and the reason a UAV would have meant nothing. A contact now
     * needs to have given itself away: shooting at you, or in your line of
     * sight. The UAV is what reveals the rest, which is the whole point of it.
     */
    const uav = this.streaks?.uavActive;
    s.enemies.length = 0;
    // Opponents only. These blips are drawn red and read as hostile, and the
    // list was every bot on the map — so in a team mode your own side showed up
    // as enemy contacts on your minimap and as enemy chevrons on your compass.
    for (const e of this.foes(this._blipSrc ??= [])) {
      s.enemies.push({
        x: e.root.position.x,
        z: e.root.position.z,
        yaw: e.yaw,
        dead: !e.alive,
        // `alert` is a bot's own AI state; `_hotUntil` is the online equivalent,
        // stamped from the server when someone shoots you.
        hot: e.alive && ((e.alert ?? 0) > 0.5 || (e._hotUntil ?? 0) > this.clockNow),
        known: !!uav || (e.alive && (e._knownUntil ?? 0) > this.clockNow),
      });
    }
    this.streaks?.decorate(s);
    this.spectator?.decorate(s);

    s.ads = adsEase;
    s.spread = this.weapon.spreadNow;
    s.ammo = this.weapon.ammo;
    s.reserve = this.weapon.reserve;
    s.magSize = this.weapon.spec.mag;
    s.weaponName = this.weapon.spec.name;
    s.reloading = this.weapon.reloading;
    s.reloadTime = this.weapon.spec.reloadTime;
    s.fireMode = this.weapon.spec.mode === 'auto' ? 'AUTO' : 'SEMI';
    s.melee = !!this.weapon.spec.melee;   // HUD hides the ammo block for melee
    // A shotgun gets its own reticle, sized from the real pellet cone — so the
    // HUD needs both the cone and the camera it is projected through.
    s.pellets = this.weapon.spec.pellets ?? 1;
    s.pelletCone = this.weapon.spec.pelletCone ?? null;
    s.fov = this.engine.camera.fov;
    s.alive = this.player.alive;
    s.health = this.player.health;
    s.maxHealth = this.player.maxHealth;
    s.yaw = this.player.yaw;
    s.level = this.level;
    s.deaths = this.deaths ?? 0;
    s.aimAtEnemy = this.aimAtEnemy;
    s.aimAtFriend = this.aimAtFriend;
    s.equipment = this._equipRow ??= [];
    this._equipRow.length = 0;
    for (const e of Object.values(this.equipment)) this._equipRow.push(e);
    s.showPrompt = this.paused && !this.hidePrompt;
    // Contextual use prompt for Crossfire's buttons and ladders.
    const btn = this.level.nearestButton?.(this.player.position);
    s.usePrompt = this.progress
      ? null
      : (this.bombPrompt ?? (btn ? 'USE' : (this.player.onLadder ? 'CLIMB' : null)));
    s.progress = this.progress
      ? { kind: this.progress.verb, t: this.progress.t / this.progress.total }
      : null;
    s.bombState = this.bomb?.state ?? null;
    s.bombFuse = this.bomb?.state === 'planted' ? this.bomb.fuse : null;
    s.hasBomb = this.bomb?.carrier === this.player;
    // Hold TAB for the board. Input already swallows Tab so the browser does
    // not move focus, but nothing ever read it — the scoreboard could not be
    // opened at all, which is why its fake roster went unnoticed for so long.
    s.scoreboard = this.input.down('Tab');

    // Everyone on the field, sorted by score, with the player marked.
    if (s.scoreboard && this.net) {
      // Online the roster is the SERVER's, and it is the only thing that knows
      // everyone's chosen name. Your own row is labelled so you can find
      // yourself in a full lobby at a glance.
      const rows = this._scoreRows ??= [];
      rows.length = 0;
      const me = this.net.client;
      const selfName = localStorage.getItem('blackout.name') || 'YOU';
      // Health rides along so the board can show how your side is holding up.
      // Knowing a teammate is on 40 is the difference between pushing with them
      // and pushing instead of them, and there is nowhere else to learn it.
      const maxHp = me.maxHealth ?? this.player.maxHealth;
      rows.push({
        name: `${selfName} (you)`, k: me.selfKills ?? 0, d: me.selfDeaths ?? 0,
        sc: (me.selfKills ?? 0) * 100, me: true, team: me.team ?? null,
        hp: Math.round(this.player.health), maxHp, dead: !this.player.alive,
      });
      for (const r of me.remotes.values()) {
        rows.push({
          name: r.name, k: r.kills, d: r.deaths, sc: r.kills * 100,
          me: false, team: r.team, dead: !r.alive,
          hp: Math.round(r.health ?? 0), maxHp: r.maxHealth ?? maxHp,
        });
      }
      // Anyone still in the lobby choosing a side: they are on the server and
      // should be visible, even though they have no body in the world yet.
      for (const p of me.roster?.players ?? []) {
        if (p.i === me.selfId) continue;
        if (me.remotes.has(p.i)) continue;
        rows.push({ name: p.n, k: p.k, d: p.d, sc: p.k * 100, me: false, team: p.t, dead: true });
      }
      s.scores = rows;
    } else if (s.scoreboard) {
      const rows = this._scoreRows ??= [];
      rows.length = 0;
      rows.push({
        name: 'YOU', k: this.hud.kills, d: this.deaths, sc: this.hud.score,
        me: true, team: this.player.team ?? null,
      });
      for (const e of this.enemies) {
        if (e._removed) continue;
        rows.push({
          name: e.name, k: e.kills, d: e.deaths, sc: e.kills * 100,
          me: false, team: e.team, dead: !e.alive,
        });
      }
      s.scores = rows;
    }

    // Online the SERVER's round state is the only one that means anything; the
    // local mode object is never ticked, so asking it would report the state it
    // was constructed with forever.
    if (this.net) this.net.decorateHud(s);
    else this.mode?.decorateHud?.(s);
    // Drawn last and over everything. `decorateHud` above is what turns it on,
    // so this reads the state it just set rather than a frame-old one.
    s.matchAwards = this.matchAwards.active ? this.matchAwards : null;

    // Project the bomb into normalised device coords for the HUD marker. The
    // camera lives here, not in the HUD, and the mode should not do view maths.
    if (s.bombWorldPos) {
      _v.copy(s.bombWorldPos);
      const cam = this.engine.camera;
      _v.project(cam);
      // `project` mirrors x/y for points behind the camera; keep the sign
      // information so the HUD can clamp an off-screen marker to the right edge.
      _dir.copy(s.bombWorldPos).sub(cam.position);
      cam.getWorldDirection(_muzzle);
      s.bombNDC = { x: _v.x, y: _v.y, z: _dir.dot(_muzzle) };
    } else {
      s.bombNDC = null;
    }
    return s;
  }

  /** True when the crosshair is currently over a live enemy. */
  _updateAimTarget() {
    const cam = this.engine.camera;
    cam.getWorldDirection(_dir);
    const origin = cam.position;
    let closest = this.physics.raycast(origin, _dir, 200)?.distance ?? 200;
    this.aimAtEnemy = false;
    this.aimAtFriend = false;
    for (const e of this.actors) {
      const h = e.raycastHit(origin, _dir, closest);
      if (!h) continue;
      closest = h.distance;
      // Whose head is under the crosshair matters: the reticle used to flag any
      // bot at all, so it lit up the same way for a teammate as for a target.
      const friendly = !!this.player.team && e.team === this.player.team;
      this.aimAtEnemy = !friendly;
      this.aimAtFriend = friendly;
    }
  }

  render(dt) {
    const { renderer, scene, camera, viewmodelScene, viewmodelCamera } = this.engine;
    // No viewmodel while dead — you are looking out of a body on the floor.
    this.weapon.group.visible = this.player.alive;
    renderer.info.reset();
    // Trace this frame's passes if the watchdog is armed. Reset per frame so
    // the array always holds exactly the frame being examined.
    if (this._passTrace) { this._passTrace.length = 0; this._tracing = true; }
    this.postfx.render(dt, this.engine.elapsed);
    this._tracing = false;
    // Viewmodel on top, sharing the depth-cleared default framebuffer.
    renderer.autoClear = false;
    renderer.clearDepth();
    renderer.render(viewmodelScene, viewmodelCamera);
    renderer.autoClear = true;
    // Still inside the same frame, so the drawing buffer is readable and
    // toDataURL() will capture it even without preserveDrawingBuffer.
    if (this._watchdog) this._watchdog();
  }

  /**
   * Catches a rendering artefact that only lasts a frame or two.
   *
   * The reported black square flashes for a few milliseconds, which no human
   * can screenshot and no headless run has reproduced across four maps, both
   * pixel ratios, live resizes and every pass toggled. So the game watches for
   * it instead: a handful of pixels per frame, and when the centre of the
   * screen is far darker than its edges, it grabs the frame AND the state that
   * produced it before either can vanish.
   *
   * Off unless `?watch=1` — each `readPixels` stalls the pipeline waiting on
   * the GPU, which is fine for a diagnostic session and not fine to ship on.
   */
  _installWatchdog() {
    const renderer = this.engine.renderer;
    const canvas = renderer.domElement;
    const gl = renderer.getContext();
    const px = new Uint8Array(4);
    const lum = (fx, fy) => {
      gl.readPixels(Math.round(canvas.width * fx), Math.round(canvas.height * fy),
        1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px);
      return px[0] * 0.299 + px[1] * 0.587 + px[2] * 0.114;
    };

    const hits = [];
    window.__artefacts = hits;
    let cooldown = 0;
    let saved = 0;

    this._installPassTrace();

    this._watchdog = () => {
      if (cooldown > 0) { cooldown--; return; }
      // gl coordinates are bottom-up. Centre points avoid the weapon (lower
      // right) and the HUD; edge points sit outside any plausible square.
      const centre = (lum(0.42, 0.66) + lum(0.56, 0.66) + lum(0.49, 0.52)) / 3;
      // Brightest of several edge points: the question is whether ANY part of
      // the frame outside the middle is still showing a lit world.
      const edge = Math.max(lum(0.02, 0.5), lum(0.98, 0.5), lum(0.5, 0.97),
        lum(0.08, 0.9), lum(0.92, 0.9));
      // Published every frame so the watchdog itself can be sanity-checked.
      // readPixels is only valid inside the frame, so this is the only place
      // these numbers can be obtained at all.
      this._watchLast = {
        centre: +centre.toFixed(1), edge: +edge.toFixed(1),
        triangles: renderer.info.render.triangles,
        programs: renderer.info.programs?.length ?? 0,
      };
      // Keep the PREVIOUS frame's program count too, so a compile that happened
      // on the bad frame itself is visible rather than already folded in.
      this._watchProgramsPrev = this._watchPrograms ?? this._watchLast.programs;
      this._watchPrograms = this._watchLast.programs;

      // Rolling baseline over the last ~20 frames. A single previous frame is
      // too fragile — the reference measurement has to survive the player
      // walking through shade, and absolute thresholds do not work at all
      // because the edges of the screen are legitimately dark most of the time.
      // Geometry actually drawn this frame. This is the strongest signal of the
      // lot: the artefact was first reported as black and then as SEE-THROUGH,
      // and "see-through" means a chunk of the level did not draw at all. A
      // pixel test has to guess at colour; the triangle count does not care
      // whether the hole is black or transparent, only that geometry vanished.
      const tris = renderer.info.render.triangles;
      const calls = renderer.info.render.calls;
      const programs = renderer.info.programs?.length ?? 0;

      const hist = this._watchHist ??= [];
      hist.push({ centre, edge, tris, calls });
      if (hist.length > 20) hist.shift();
      if (hist.length < 8) return;
      const median = (key) => {
        const v = hist.slice(0, -1).map((h) => h[key]).sort((a, b) => a - b);
        return v[v.length >> 1];
      };
      const baseC = median('centre');
      const baseE = median('edge');
      const baseT = median('tris');
      const baseCalls = median('calls');

      // The artefact is a TRANSIENT: for a frame or two something large stops
      // drawing while the rest of the frame carries on unchanged.
      const restStable = Math.abs(edge - baseE) < Math.max(14, baseE * 0.5);
      // 1. The middle collapses to black.
      const wentBlack = centre < 8 && baseC > centre * 3 + 8;
      // 2. Or the middle changes violently in either direction — the
      //    "transparent" variant, where the hole shows whatever is behind.
      const jumped = baseC > 12 && Math.abs(centre - baseC) > Math.max(45, baseC * 1.6);
      // 3. Or a big slice of the world simply did not render.
      const lostGeometry = baseT > 20000 && tris < baseT * 0.65;
      const lostCalls = baseCalls > 12 && calls < baseCalls * 0.6;

      const reason = lostGeometry ? 'geometry-missing'
        : lostCalls ? 'draw-calls-missing'
          : (wentBlack && restStable) ? 'centre-black'
            : (jumped && restStable) ? 'centre-jumped' : null;
      if (!reason) return;
      this._watchHist = [];              // don't re-fire on our own bad frame

      cooldown = 30;                       // don't fill the disk with one event
      const state = window.__game.diagnostics();
      state.trigger = {
        reason,
        centre: +centre.toFixed(1), baselineCentre: +baseC.toFixed(1),
        edge: +edge.toFixed(1), baselineEdge: +baseE.toFixed(1),
        triangles: tris, baselineTriangles: baseT,
        calls, baselineCalls: baseCalls,
        // A shader compiling mid-frame stalls the driver and is a known way to
        // drop geometry for a frame or two, so record whether one happened.
        programs, programsCompiledThisFrame: programs - (this._watchProgramsPrev ?? programs),
      };
      state.frame = renderer.info.render ? { ...renderer.info.render } : null;
      // The pass-by-pass trace of THIS frame, captured as it rendered. This is
      // the measurement that actually names the culprit; the bisect below is a
      // fallback that only works if the artefact happens to persist.
      state.passTrace = this._passTrace ? [...this._passTrace] : null;
      state.culprit = this._bisectFrame(lum);
      let png = null;
      try { png = canvas.toDataURL('image/png'); } catch { /* tainted, ignore */ }
      hits.push({ state, png });
      console.warn('[watchdog] artefact frame captured', state);

      // First few get written to disk automatically — by the time anyone reads
      // the console the moment has passed.
      if (saved < 3) {
        saved++;
        const stamp = `${saved}`;
        if (png) {
          const a = document.createElement('a');
          a.href = png;
          a.download = `artefact-${stamp}.png`;
          a.click();
        }
        const blob = new Blob([JSON.stringify(state, null, 2)], { type: 'application/json' });
        const a2 = document.createElement('a');
        a2.href = URL.createObjectURL(blob);
        a2.download = `artefact-${stamp}.json`;
        a2.click();
      }
    };
    console.log('%c[watchdog] armed — the next black frame is captured automatically',
      'color:#ffb020');
  }

  /**
   * Samples the middle of the image after every post pass, every frame.
   *
   * Re-rendering after the fact cannot catch a transient — proven twice now,
   * once in a staged test and once in the user's own captures, where the
   * bisect's control frame measured 75-128 while the real frame measured 0.
   * Whatever goes wrong is gone by the time anything can be re-run.
   *
   * So the frame is instrumented as it happens: after each pass, read one
   * pixel out of the buffer that pass just wrote. When the watchdog fires, the
   * trace for that exact frame is already in hand and reads like
   *   RenderPass 91 -> GTAOPass 0 -> ...
   * which names the pass that blacked it out, with no inference at all.
   */
  _installPassTrace() {
    const fx = this.postfx;
    const renderer = this.engine.renderer;
    if (!fx?.composer || fx._traced) return;
    fx._traced = true;

    const buf = new Uint8Array(4);
    const sample = () => {
      // Whatever framebuffer the pass left bound is the one carrying its
      // output — including null, which means it wrote straight to the screen.
      const target = renderer.getRenderTarget();
      const w = target ? target.width : renderer.domElement.width;
      const h = target ? target.height : renderer.domElement.height;
      const x = Math.round(w * 0.5), y = Math.round(h * 0.5);
      try {
        if (target) renderer.readRenderTargetPixels(target, x, y, 1, 1, buf);
        else renderer.getContext().readPixels(x, y, 1, 1,
          renderer.getContext().RGBA, renderer.getContext().UNSIGNED_BYTE, buf);
      } catch { return -1; }
      return Math.round(buf[0] * 0.299 + buf[1] * 0.587 + buf[2] * 0.114);
    };

    // Minified builds rename classes and there are two anonymous ShaderPasses,
    // so identify them by the field they were stored in rather than by type.
    const named = new Map([[fx.gtao, 'gtao'], [fx.aerial, 'aerial'], [fx.bloom, 'bloom'],
      [fx.output, 'output'], [fx.smaa, 'smaa'], [fx.grade, 'grade'],
      [fx.renderPass, 'renderPass']]);

    this._passTrace = [];
    fx.composer.passes.forEach((pass, i) => {
      const orig = pass.render.bind(pass);
      const label = `${i}:${named.get(pass) ?? pass.constructor.name}`;
      pass.render = (...args) => {
        orig(...args);
        if (this._tracing) this._passTrace.push({ pass: label, centre: sample() });
      };
    });
  }

  /**
   * Automated bisect, run on the frame the artefact appears.
   *
   * Four rounds of guessing which object draws the square have now been wrong
   * (a shader compile, missing geometry, GTAO's G-buffer, the muzzle-flash
   * card — each ruled out by measurement). The two captured frames show the
   * square at DIFFERENT screen positions with the SAME size, so it is a world
   * object near the camera, not a screen-space pass.
   *
   * So: hide each top-level scene object in turn, re-render the world, and see
   * which one's absence brings the middle of the screen back. That names the
   * culprit outright instead of narrowing the field. It costs one extra render
   * per object on a single frame, which is a hitch nobody will see once and is
   * worth infinitely more than another hypothesis.
   */
  _bisectFrame(lum) {
    const { renderer, scene, camera } = this.engine;
    const fx = this.postfx;
    const centre = () => (lum(0.42, 0.66) + lum(0.56, 0.66) + lum(0.49, 0.52)) / 3;

    // Every re-render below MUST go through the post stack. The first version
    // of this rendered the raw scene instead, and the captured evidence showed
    // why that was useless: its control frame measured 75 when the real frame
    // measured 0. The artefact does not exist in the raw scene render at all —
    // it is produced by the post stack — so a bisect that skips post is
    // bisecting something that was never broken.
    const renderFull = () => {
      fx.render(1 / 60, this.engine.elapsed);
      renderer.autoClear = false;
      renderer.clearDepth();
      renderer.render(this.engine.viewmodelScene, this.engine.viewmodelCamera);
      renderer.autoClear = true;
    };

    const describe = (o) => ({
      name: o.name || '(unnamed)',
      type: o.type,
      material: o.material?.type ?? (Array.isArray(o.material) ? 'multi' : null),
      geometry: o.geometry?.type ?? null,
      childCount: o.children.length,
      pos: o.position.toArray().map((v) => +v.toFixed(2)),
      distToCamera: +o.position.distanceTo(camera.position).toFixed(2),
    });

    const passes = [];
    const objects = [];
    try {
      renderFull();
      const base = centre();
      // If the artefact does not survive into the bisect's own re-render there
      // is nothing to bisect, and saying so is far better than naming an
      // innocent object.
      const reproduced = base < 12;

      // 1. Which post pass draws it. This is the cheap, decisive test: seven
      //    renders, and the evidence says the answer is in here.
      if (reproduced) {
        for (const p of fx.composer.passes) {
          if (!p.enabled) continue;
          p.enabled = false;
          renderFull();
          const without = centre();
          p.enabled = true;
          passes.push({
            pass: p.constructor.name,
            centreWithout: +without.toFixed(1),
            restores: without > base + 18 && without > 25,
          });
        }
      }

      // 2. Only if no pass explains it, fall back to scene objects — still
      //    rendered through post, so the measurement means something.
      if (reproduced && !passes.some((p) => p.restores)) {
        for (const child of [...scene.children]) {
          if (!child.visible) continue;
          child.visible = false;
          renderFull();
          const without = centre();
          child.visible = true;
          if (without > base + 18 && without > 25) {
            objects.push({ ...describe(child), centreWithout: +without.toFixed(1) });
          }
        }
        objects.sort((a, b) => b.centreWithout - a.centreWithout);
      }

      return {
        baseCentre: +base.toFixed(1),
        reproducedInBisect: reproduced,
        passes,
        suspects: objects.slice(0, 6),
      };
    } catch (e) {
      return { error: String(e) };
    } finally {
      // Leave the framebuffer showing the artefact frame again so the PNG that
      // gets saved is the real one, not a bisect render.
      try {
        renderer.setRenderTarget(null);
        this.postfx.render(1 / 60, this.engine.elapsed);
        renderer.autoClear = false;
        renderer.clearDepth();
        renderer.render(this.engine.viewmodelScene, this.engine.viewmodelCamera);
        renderer.autoClear = true;
      } catch { /* the capture still has the state dump */ }
    }
  }

  _loop() {
    requestAnimationFrame(this._loop);
    const dt = Math.min(0.05, this.engine.clock.getDelta());
    this.engine.elapsed += dt;

    let adsEase = this.weapon.ads;
    if (!this.paused) adsEase = this.update(dt);

    this.render(dt);

    // Paged with the arrow keys, so it is stepped even while the world is
    // paused — during the intermission there is nothing else to do, and a
    // screen that only responds while unpaused would look broken.
    this.matchAwards.update(dt, this.input);

    const st = this._hudState(adsEase);
    st.telemetry = {
      fps: this._updateFps(dt),
      // Only meaningful online; offline there is no server to be late.
      ping: this.net ? this.net.client.ping : null,
      room: this.net ? (this.net.client.roomInfo?.id ?? null) : null,
      roomName: this.net ? (this.net.client.roomInfo?.name ?? null) : null,
    };
    this.hud.draw(dt, st);

    this.input.endFrame();
  }
}

/**
 * Distance between two actors, whichever kind they are.
 *
 * The player is a capsule with a `position`; a bot is a rig with a
 * `root.position`. Every stats call site would otherwise have to know which it
 * was holding.
 */
function actorPos(a) { return a?.position ?? a?.root?.position ?? null; }
function aimDistance(a, b) {
  const p = actorPos(a), q = actorPos(b);
  return p && q ? p.distanceTo(q) : 0;
}

/* -------------------------------------------------------- map select ----- */

const MAPS = [
  {
    id: 'blackout',
    name: 'AL-RASHEED DISTRICT',
    sub: 'Original · urban block',
    blurb: 'Three-lane street fight. Long sightlines down the carriageway, '
      + 'flanking routes through the arcades, vertical cover on the containers.',
    build: (engine) => new Level(engine),
  },
  {
    id: 'crossfire',
    name: 'CROSSFIRE',
    sub: 'Half-Life · Dario Casali · 1998',
    blurb: 'Reconstructed from the original Hammer brush source. Compound and '
      + 'courtyard on one side, Satellite Operations bunker and the double '
      + 'helipad on the other, joined by the long tunnel.',
    build: (engine) => CrossfireLevel.load(engine),
  },
  {
    id: 'assault',
    name: 'ASSAULT',
    sub: 'Counter-Strike · CryptR · 1999',
    blurb: 'Reconstructed from the compiled GoldSrc BSP. Warehouse and yard '
      + 'either side of the street, with the roof, the vents and the sewer '
      + 'tunnel as the three ways in.',
    build: (engine) => AssaultLevel.load(engine),
  },
  {
    id: 'dust2',
    name: 'DUST II',
    sub: 'Counter-Strike · 1999',
    blurb: 'Reconstructed from the compiled GoldSrc BSP. Long A, the tunnels '
      + 'to B, and mid with the double doors — the most played competitive '
      + 'layout ever built.',
    build: (engine) => Dust2Level.load(engine),
  },
];

/** Renders the deployment-zone picker and resolves with the chosen map. */
/**
 * Generic card picker. Every selection screen is this — the `.mapselect / .ms-*`
 * CSS is reused verbatim, and the card grid is `auto-fit`, so a screen with two
 * cards needs no new styling.
 *
 * Resolves with the chosen item. The overlay removes itself unless `keep` is
 * set, so chaining screens does not leave scrims stacked on top of each other.
 *
 * @param {{title:string, sub:string, items:Array, cta?:string, keep?:boolean}} opts
 */
function cardSelect(container, { title, sub, items, cta = 'DEPLOY', keep = false }) {
  const el = document.createElement('div');
  el.className = 'mapselect';
  el.innerHTML = `
    <div class="ms-inner">
      <div class="ms-title">${title}</div>
      <div class="ms-sub">${sub}</div>
      <div class="ms-cards">
        ${items.map((m, i) => `
          <button class="ms-card${m.disabled ? ' is-disabled' : ''}" data-i="${i}"${m.team ? ` data-team="${m.team}"` : ''}${m.disabled ? ' disabled' : ''}>
            <span class="ms-name">${m.name}</span>
            <span class="ms-meta">${m.sub}</span>
            <span class="ms-blurb">${m.blurb}</span>
            <span class="ms-go">${m.disabled ? 'UNAVAILABLE' : cta}</span>
          </button>`).join('')}
      </div>
      <div class="ms-status" role="status"></div>
    </div>`;
  container.appendChild(el);

  return new Promise((resolve) => {
    el.addEventListener('click', (ev) => {
      const card = ev.target.closest('.ms-card');
      if (!card || card.disabled) return;
      el.querySelectorAll('.ms-card').forEach((c) => { c.disabled = true; });
      card.classList.add('is-loading');
      if (keep) el.querySelector('.ms-status').textContent = 'LOADING…';
      const item = items[+card.dataset.i];
      if (!keep) el.remove();
      resolve({ item, dismiss: () => el.remove() });
    });
  });
}

/**
 * Server address + password. Its own screen because it is a form, not a choice
 * between cards, and because a wrong password has to be reported here rather
 * than dumping the player into a black screen.
 *
 * The password is remembered only if asked for. The address always is —
 * retyping a host every match is friction with no security value.
 */
function connectSelect(container, { map, mode }) {
  const el = document.createElement('div');
  el.className = 'mapselect';
  const savedUrl = localStorage.getItem('blackout.server') ?? '';
  const savedPass = localStorage.getItem('blackout.pass') ?? '';
  const savedName = localStorage.getItem('blackout.name') ?? '';
  el.innerHTML = `
    <div class="ms-inner" style="width:min(560px,92vw)">
      <div class="ms-title">MULTIPLAYER</div>
      <div class="ms-sub">${map.name} · ${mode.name}</div>
      <div style="display:grid;gap:12px;text-align:left;margin-top:26px">
        <label class="ms-blurb">SERVER
          <input id="mp-url" value="${savedUrl}" placeholder="wss://host:9443"
            style="width:100%;margin-top:6px;padding:10px;background:#0d1116;color:#e8eef5;
                   border:1px solid rgba(255,255,255,.16);border-radius:3px;font:inherit"></label>
        <label class="ms-blurb">PASSWORD
          <input id="mp-pass" type="password" value="${savedPass}"
            style="width:100%;margin-top:6px;padding:10px;background:#0d1116;color:#e8eef5;
                   border:1px solid rgba(255,255,255,.16);border-radius:3px;font:inherit"></label>
        <label class="ms-blurb">CALLSIGN
          <input id="mp-name" value="${savedName}" maxlength="16" placeholder="OPERATOR"
            style="width:100%;margin-top:6px;padding:10px;background:#0d1116;color:#e8eef5;
                   border:1px solid rgba(255,255,255,.16);border-radius:3px;font:inherit"></label>
        <label class="ms-blurb" style="display:flex;gap:8px;align-items:center">
          <input id="mp-remember" type="checkbox" ${savedPass ? 'checked' : ''}> remember password</label>
      </div>
      <div class="ms-cards" style="margin-top:22px">
        <button class="ms-card" id="mp-go">
          <span class="ms-name">CONNECT</span>
          <span class="ms-meta">JOIN SERVER</span>
          <span class="ms-go">ENTER</span>
        </button>
        <button class="ms-card" id="mp-back">
          <span class="ms-name">BACK</span>
          <span class="ms-meta">PLAY WITH BOTS</span>
          <span class="ms-go">OFFLINE</span>
        </button>
      </div>
      <div class="ms-status" role="status"></div>
    </div>`;
  container.appendChild(el);
  const $ = (id) => el.querySelector(id);
  const status = $('.ms-status');
  $('#mp-url').focus();

  return new Promise((resolve) => {
    const go = () => {
      const url = $('#mp-url').value.trim();
      if (!url) { status.textContent = 'ENTER A SERVER ADDRESS'; return; }
      // A page served over HTTPS cannot open a ws:// socket — the browser blocks
      // it as mixed content. Saying so here beats an unexplained failure.
      if (location.protocol === 'https:' && url.startsWith('ws://')) {
        status.textContent = 'THIS PAGE IS HTTPS — USE wss://';
        return;
      }
      localStorage.setItem('blackout.server', url);
      localStorage.setItem('blackout.name', $('#mp-name').value.trim());
      if ($('#mp-remember').checked) localStorage.setItem('blackout.pass', $('#mp-pass').value);
      else localStorage.removeItem('blackout.pass');
      resolve({
        online: true, url, password: $('#mp-pass').value,
        name: $('#mp-name').value.trim(),
        status: (t) => { status.textContent = t; },
        dismiss: () => el.remove(),
      });
    };
    $('#mp-go').addEventListener('click', go);
    $('#mp-back').addEventListener('click', () => { el.remove(); resolve({ online: false }); });
    el.addEventListener('keydown', (e) => { if (e.key === 'Enter') go(); });
  });
}

/**
 * The server browser: what is running right now.
 *
 * Public — no password to LOOK. You need the password only for the room you
 * were actually invited to, which is what makes "here is the room and here is
 * the password" a single message to your friends rather than two.
 */
/**
 * The master password: the door to multiplayer itself.
 *
 * Asked once, before the room list, because the list is no longer public — you
 * cannot see what is running until the server knows who you are. It is
 * remembered, so this screen is a one-off for anyone who plays here regularly,
 * which is the point of a password that never changes.
 */
function masterGate(container, defaultUrl) {
  const el = document.createElement('div');
  el.className = 'mapselect';
  const saved = localStorage.getItem('blackout.master') ?? '';
  const url = localStorage.getItem('blackout.server') || defaultUrl;
  el.innerHTML = `
    <div class="ms-inner" style="width:min(560px,92vw)">
      <div class="ms-title">MULTIPLAYER</div>
      <div class="ms-sub">SERVER PASSWORD</div>
      <div style="display:grid;gap:12px;text-align:left;margin-top:24px">
        <label class="ms-blurb">SERVER
          <input id="mg-url" value="${escapeHtml(url)}"
            style="width:100%;margin-top:6px;padding:10px;background:#0d1116;color:#e8eef5;
                   border:1px solid rgba(255,255,255,.16);border-radius:3px;font:inherit"></label>
        <label class="ms-blurb">MASTER PASSWORD
          <input id="mg-pass" type="password" value="${escapeHtml(saved)}"
            style="width:100%;margin-top:6px;padding:10px;background:#0d1116;color:#e8eef5;
                   border:1px solid rgba(255,255,255,.16);border-radius:3px;font:inherit"></label>
      </div>
      <div class="ms-cards" style="margin-top:20px">
        <button class="ms-card" id="mg-go"><span class="ms-name">CONTINUE</span>
          <span class="ms-meta">Server browser</span><span class="ms-go">UNLOCK</span></button>
        <button class="ms-card" id="mg-back"><span class="ms-name">BACK</span>
          <span class="ms-meta">Single player</span><span class="ms-go">OFFLINE</span></button>
      </div>
      <div class="ms-status" role="status"></div>
    </div>`;
  container.appendChild(el);
  el.querySelector('#mg-pass').focus();

  return new Promise((resolve) => {
    const status = (t) => { el.querySelector('.ms-status').textContent = t; };
    const go = async () => {
      const master = el.querySelector('#mg-pass').value;
      const u = el.querySelector('#mg-url').value.trim();
      localStorage.setItem('blackout.server', u);
      status('CHECKING…');
      try {
        // Verified here rather than deferred to the join, so a wrong password
        // is reported on the screen that asked for it.
        const rooms = await NetClient.listRooms(u, master);
        localStorage.setItem('blackout.master', master);
        el.remove();
        resolve({ master, url: u, rooms });
      } catch (e) {
        status({
          'bad-master': 'WRONG PASSWORD',
          throttled: 'TOO MANY ATTEMPTS — WAIT AND RETRY',
        }[e.message] ?? `CANNOT REACH SERVER — ${e.message}`);
      }
    };
    el.querySelector('#mg-go').addEventListener('click', go);
    el.querySelector('#mg-back').addEventListener('click', () => { el.remove(); resolve(null); });
    el.addEventListener('keydown', (e) => { if (e.key === 'Enter') go(); });
  });
}

function roomSelect(container, defaultUrl, master, seed) {
  const el = document.createElement('div');
  el.className = 'mapselect';
  container.appendChild(el);
  // The gate already fetched the list to verify the password; reusing it avoids
  // a second round trip and a visible flash of "LOADING…" on entry.
  let rooms = seed ?? [];
  let url = localStorage.getItem('blackout.server') || defaultUrl;

  const paint = (status = '') => {
    el.innerHTML = `
      <div class="ms-inner" style="width:min(820px,94vw)">
        <div class="ms-title">MULTIPLAYER</div>
        <div class="ms-sub">SERVER BROWSER · PRESS R TO REFRESH</div>
        <div class="ms-cards" style="grid-template-columns:1fr">
          ${rooms.length ? rooms.map((r, i) => `
            <button class="ms-card" data-i="${i}" ${r.count >= r.max ? 'disabled' : ''}>
              <span class="ms-name">${escapeHtml(r.name)}</span>
              <span class="ms-meta">${String(r.map).toUpperCase()} ·
                ${r.mode === 'bomb' ? 'BOMB DEFUSAL' : 'TEAM DEATHMATCH'}</span>
              <span class="ms-blurb">${r.count} / ${r.max} players${r.playing ? ` · ${r.playing} in the match` : ''}${r.locked ? ' · PASSWORD' : ''}</span>
              <span class="ms-go">${r.count >= r.max ? 'SERVER IS FULL' : (r.locked ? 'LOCKED' : 'JOIN')}</span>
            </button>`).join('')
            : `<div class="ms-blurb" style="padding:26px;text-align:center;opacity:.6">
                 No rooms are running.<br>Ask whoever runs the server to create one.</div>`}
        </div>
        <div style="margin-top:18px;text-align:left">
          <label class="ms-blurb">SERVER
            <input id="rs-url" value="${escapeHtml(url)}"
              style="width:100%;margin-top:6px;padding:9px;background:#0d1116;color:#e8eef5;
                     border:1px solid rgba(255,255,255,.16);border-radius:3px;font:inherit"></label>
        </div>
        <div class="ms-cards" style="margin-top:14px">
          <button class="ms-card" id="rs-refresh">
            <span class="ms-name">REFRESH</span><span class="ms-meta">R</span>
            <span class="ms-go">RELOAD LIST</span></button>
          <button class="ms-card" id="rs-back">
            <span class="ms-name">BACK</span><span class="ms-meta">Single player</span>
            <span class="ms-go">OFFLINE</span></button>
        </div>
        <div class="ms-status" role="status">${status}</div>
      </div>`;
  };

  const refresh = async () => {
    url = (el.querySelector('#rs-url')?.value || url).trim();
    localStorage.setItem('blackout.server', url);
    paint('LOADING…');
    try {
      rooms = await NetClient.listRooms(url, master);
      paint('');
    } catch (e) {
      rooms = [];
      paint(`CANNOT REACH SERVER — ${e.message}`);
    }
  };
  if (seed) paint(''); else refresh();

  return new Promise((resolve) => {
    const onKey = (e) => {
      if (e.key === 'r' || e.key === 'R') { if (!(e.target instanceof HTMLInputElement)) refresh(); }
    };
    addEventListener('keydown', onKey);
    const done = (v) => { removeEventListener('keydown', onKey); resolve(v); };

    el.addEventListener('click', (ev) => {
      if (ev.target.closest('#rs-refresh')) return refresh();
      if (ev.target.closest('#rs-back')) { el.remove(); return done(null); }
      const card = ev.target.closest('.ms-card[data-i]');
      if (!card || card.disabled) return;
      done({ room: rooms[+card.dataset.i], url, dismiss: () => el.remove(), el });
    });
  });
}

/** Ask for the password to ONE room, reporting failures in place. */
function roomPassword(container, room) {
  const el = document.createElement('div');
  el.className = 'mapselect';
  el.innerHTML = `
    <div class="ms-inner" style="width:min(520px,92vw)">
      <div class="ms-title">${escapeHtml(room.name)}</div>
      <div class="ms-sub">${String(room.map).toUpperCase()} ·
        ${room.mode === 'bomb' ? 'BOMB DEFUSAL' : 'TEAM DEATHMATCH'} ·
        ${room.count}/${room.max}</div>
      <div style="display:grid;gap:12px;text-align:left;margin-top:24px">
        <label class="ms-blurb" style="${room.locked ? '' : 'display:none'}">ROOM PASSWORD
          <input id="rp-pass" type="password" autofocus
            style="width:100%;margin-top:6px;padding:10px;background:#0d1116;color:#e8eef5;
                   border:1px solid rgba(255,255,255,.16);border-radius:3px;font:inherit"></label>
        <label class="ms-blurb">CALLSIGN
          <input id="rp-name" maxlength="16" placeholder="OPERATOR"
            value="${escapeHtml(localStorage.getItem('blackout.name') ?? '')}"
            style="width:100%;margin-top:6px;padding:10px;background:#0d1116;color:#e8eef5;
                   border:1px solid rgba(255,255,255,.16);border-radius:3px;font:inherit"></label>
      </div>
      <div class="ms-cards" style="margin-top:20px">
        <button class="ms-card" id="rp-go"><span class="ms-name">JOIN</span>
          <span class="ms-meta">ENTER ROOM</span><span class="ms-go">CONNECT</span></button>
        <button class="ms-card" id="rp-back"><span class="ms-name">BACK</span>
          <span class="ms-meta">Room list</span><span class="ms-go">RETURN</span></button>
      </div>
      <div class="ms-status" role="status"></div>
    </div>`;
  container.appendChild(el);
  // An open room asks only for a callsign — prompting for a password that does
  // not exist is a good way to make someone give up at the door.
  el.querySelector(room.locked ? '#rp-pass' : '#rp-name').focus();

  return new Promise((resolve) => {
    const go = () => resolve({
      password: el.querySelector('#rp-pass').value,
      name: el.querySelector('#rp-name').value.trim(),
      status: (t) => { el.querySelector('.ms-status').textContent = t; },
      dismiss: () => el.remove(),
    });
    el.querySelector('#rp-go').addEventListener('click', go);
    el.querySelector('#rp-back').addEventListener('click', () => { el.remove(); resolve(null); });
    el.addEventListener('keydown', (e) => { if (e.key === 'Enter') go(); });
  });
}

/**
 * The lobby: who is already here, and on which side, before you commit.
 *
 * Shown only after a successful connection, because the counts and names have
 * to be real. It also carries the map and mode, which the SERVER decides — one
 * room means there is nothing for the client to choose.
 */
function lobbySelect(container, client) {
  const el = document.createElement('div');
  el.className = 'mapselect';
  container.appendChild(el);

  const render = () => {
    const r = client.roster ?? { players: [], count: 0, max: 12, map: '?', mode: '?' };
    const side = (t) => r.players.filter((p) => p.t === t);
    const list = (arr) => arr.length
      ? arr.map((p) => `<div style="padding:3px 0">${escapeHtml(p.n)}${p.i === client.selfId ? ' <span style="color:#ffb020">(you)</span>' : ''}</div>`).join('')
      : '<div style="opacity:.4;padding:3px 0">—</div>';
    const waiting = side(null);
    el.innerHTML = `
      <div class="ms-inner" style="width:min(760px,94vw)">
        <div class="ms-title">${String(r.map).toUpperCase()}</div>
        <div class="ms-sub">${r.mode === 'bomb' ? 'BOMB DEFUSAL' : 'TEAM DEATHMATCH'} ·
          ${r.count} / ${r.max} PLAYERS</div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:18px;margin-top:24px;text-align:left">
          <div>
            <div class="ms-meta" style="color:#e0503a">ATTACKERS (${side('red').length})</div>
            <div class="ms-blurb">${list(side('red'))}</div>
          </div>
          <div>
            <div class="ms-meta" style="color:#5590e8">DEFENDERS (${side('blue').length})</div>
            <div class="ms-blurb">${list(side('blue'))}</div>
          </div>
        </div>
        ${waiting.length ? `<div class="ms-blurb" style="margin-top:14px;text-align:left;opacity:.6">
          CHOOSING: ${waiting.map((p) => escapeHtml(p.n)).join(', ')}</div>` : ''}
        <div class="ms-cards" style="margin-top:24px">
          <button class="ms-card" data-team="red">
            <span class="ms-name">ATTACKERS</span>
            <span class="ms-meta">${side('red').length} on this side</span>
            <span class="ms-go">JOIN</span>
          </button>
          <button class="ms-card" data-team="blue">
            <span class="ms-name">DEFENDERS</span>
            <span class="ms-meta">${side('blue').length} on this side</span>
            <span class="ms-go">JOIN</span>
          </button>
        </div>
        <div class="ms-status" role="status"></div>
      </div>`;
  };
  render();
  // Live: someone joining or picking a side updates this screen underneath you.
  client.onRoster = render;

  return new Promise((resolve) => {
    el.addEventListener('click', async (ev) => {
      const card = ev.target.closest('.ms-card');
      if (!card) return;
      el.querySelectorAll('.ms-card').forEach((c) => { c.disabled = true; });
      el.querySelector('.ms-status').textContent = 'JOINING…';
      try {
        await client.joinTeam(card.dataset.team);
        client.onRoster = null;
        resolve({ team: card.dataset.team, dismiss: () => el.remove() });
      } catch (e) {
        el.querySelector('.ms-status').textContent = `FAILED: ${e.message}`;
        el.querySelectorAll('.ms-card').forEach((c) => { c.disabled = false; });
      }
    });
  });
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
}

/** Bots or real people. Asked after the map, before the mode. */
const OPPONENTS = [
  {
    id: 'bots', name: 'SINGLE PLAYER', sub: 'Offline · bots',
    blurb: 'Play against AI on this machine. Choose any map and mode.',
  },
  {
    id: 'online', name: 'MULTIPLAYER', sub: 'Online · real players',
    blurb: 'Browse the rooms running on the server and join one. You will need that room\u2019s password.',
  },
];

const MODES = [
  {
    id: 'dm', name: 'DEATHMATCH', sub: 'Free-for-all',
    blurb: 'Endless respawns. Everyone is hostile. No objective but the score.',
  },
  {
    id: 'bomb', name: 'BOMB DEFUSAL', sub: 'Counter-Strike rules',
    blurb: 'Red plants at A or B; blue prevents it or defuses. One life a round.',
  },
];

const TEAMS = [
  {
    id: 'red', team: 'red', name: 'ATTACKERS', sub: 'Red · Terrorists',
    blurb: 'You carry the bomb. Reach site A or B and plant it, then hold.',
  },
  {
    id: 'blue', team: 'blue', name: 'DEFENDERS', sub: 'Blue · Counter-Terrorists',
    blurb: 'Stop the plant. If it goes down, defuse it before the fuse runs out.',
  },
];

/**
 * A tiny portrait of an operator's KIT, for the picker's slot chips.
 *
 * The chips used to be flat colour swatches, which works in deathmatch and is
 * useless in bomb defusal: the whole point of a team palette is that all six of
 * a side wear the same colour, so all six swatches were identical and the list
 * looked broken. What actually differs between the six is the gear, so that is
 * what the chip draws — a head-on silhouette built from the same flags the
 * model is built from, on the operator's own colour.
 *
 * SVG rather than a second `OperatorStage`: twelve live WebGL contexts for
 * twelve list rows would blow the browser's context budget on its own.
 */
function operatorGlyph(o, ffa) {
  const p = ffa ? o.solo : o.team;
  const g = o.gear;
  const skin = g.balaclava || g.shemagh ? p.gear : p.skin;
  const helmetFill = g.helmetCover ? p.camo[0] : p.gear;
  const parts = [];

  // Shoulders, which carry the build: the heavy and slim operators differ here
  // by more than anything above the neck.
  const sw = 9.5 * (g.build ?? 1);
  parts.push(`<path d="M${12 - sw} 24 q0 -5 ${sw} -5 q${sw} 0 ${sw} 5 Z" fill="${p.camo[2]}"/>`);
  parts.push(`<rect x="7.5" y="14.5" width="9" height="5" rx="1.6" fill="${p.gear}"/>`);

  // Head.
  parts.push(`<ellipse cx="12" cy="10" rx="5.2" ry="6" fill="${p.skin}"/>`);
  if (g.beard) parts.push(`<path d="M7.2 11.5 q4.8 6.5 9.6 0 q-1 4.5 -4.8 4.5 q-3.8 0 -4.8 -4.5 Z" fill="#3a2b20"/>`);
  // A covered lower face reads as a solid block from the chin to the nose.
  if (g.balaclava) parts.push(`<path d="M6.9 9.5 h10.2 v3.6 q0 3 -5.1 3 q-5.1 0 -5.1 -3 Z" fill="${skin}"/>`);
  if (g.shemagh) parts.push(`<path d="M6.6 9.8 h10.8 l-1.4 5.6 q-3.4 2 -8 0 Z" fill="${p.camo[3]}" opacity="0.92"/>`);
  if (g.goggles) parts.push(`<rect x="6.2" y="8.2" width="11.6" height="2.6" rx="1.2" fill="${p.accent}"/>`);
  if (g.helmet) {
    parts.push(`<path d="M5.9 10.2 a6.1 6.1 0 0 1 12.2 0 z" fill="${helmetFill}"/>`);
    parts.push(`<rect x="5.4" y="9.6" width="13.2" height="1.5" rx="0.7" fill="${p.gear}"/>`);
  }
  if (g.nvg) {
    parts.push(`<rect x="10.6" y="3.4" width="2.8" height="3.2" rx="0.7" fill="#2b2f34"/>`);
    parts.push(`<circle cx="12" cy="3.2" r="1.5" fill="#4b5158"/>`);
  }
  if (g.counterweight) parts.push(`<rect x="15.6" y="6.4" width="3" height="2.4" rx="0.8" fill="#2b2f34"/>`);
  if (g.antenna) parts.push(`<path d="M19 18 L21.4 5" stroke="#2b2f34" stroke-width="1.1" fill="none"/>`);

  return `<svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true">${parts.join('')}</svg>`;
}

/**
 * Pick an operator.
 *
 * Six per side in bomb defusal, all twelve in deathmatch — see
 * src/gameplay/operators.js for why those are different lists rather than the
 * same one filtered.
 *
 * The choice is remembered per side. Nobody wants to re-pick a cosmetic every
 * time they start a match, and remembering it per side rather than globally is
 * what makes "my attacker" and "my defender" two separate answers, which is how
 * players actually think about it.
 *
 * @param {'red'|'blue'} side
 * @param {boolean} ffa deathmatch — the flat twelve, in their own colours
 */
function operatorSelect(container, { side = 'red', ffa = false, sub = '' } = {}) {
  // In deathmatch there are no sides, so the twelve are numbered 01-12 and the
  // ATTACKER/DEFENDER tag is dropped — it would be describing a distinction the
  // mode does not have.
  const list = ffa ? ALL_OPERATORS : OPERATORS[side];
  const storeKey = `blackout.op.${ffa ? 'ffa' : side}`;
  const saved = Number(localStorage.getItem(storeKey));
  let index = Number.isFinite(saved) && saved >= 0 && saved < list.length ? saved : 0;

  const el = document.createElement('div');
  el.className = 'mapselect';
  el.innerHTML = `
    <div class="ms-inner" style="width:min(1000px,94vw)">
      <div class="ms-title">SELECT OPERATOR</div>
      <div class="ms-sub">${escapeHtml(sub || (ffa ? 'FREE FOR ALL' : side === 'red' ? 'ATTACKERS' : 'DEFENDERS'))}</div>
      <div class="op-body">
        <div class="op-stage" id="op-stage"><div class="op-hint">DRAG TO TURN</div></div>
        <div>
          <div class="op-list" id="op-list">
            ${list.map((o, i) => `
              <button class="op-slot" data-i="${i}" aria-selected="${i === index}">
                <span class="op-chip" style="background:${(ffa ? o.solo : o.team).camo[1]};
                  outline:1px solid ${(ffa ? o.solo : o.team).accent}">${operatorGlyph(o, ffa)}</span>
                <span>
                  <span class="op-num">${ffa ? String(i + 1).padStart(2, '0') : o.label}</span>
                  <span class="op-side">${ffa ? '' : o.side === 'red' ? 'ATTACKER' : 'DEFENDER'}</span>
                  <div class="op-kit">${o.kit}</div>
                </span>
              </button>`).join('')}
          </div>
          <div class="op-blurb" id="op-blurb"></div>
        </div>
      </div>
      <div class="ms-cards" style="margin-top:18px;grid-template-columns:1fr;max-width:340px;margin-left:auto;margin-right:auto">
        <button class="ms-card" id="op-go">
          <span class="ms-name">DEPLOY</span>
          <span class="ms-meta">CONFIRM OPERATOR</span>
          <span class="ms-go">ENTER</span>
        </button>
      </div>
    </div>`;
  container.appendChild(el);

  const stage = new OperatorStage({ width: 480, height: 720 });
  const host = el.querySelector('#op-stage');
  host.insertBefore(stage.canvas, host.firstChild);
  stage.enableDrag(host);
  stage.start();

  const paint = () => {
    for (const b of el.querySelectorAll('.op-slot')) {
      b.setAttribute('aria-selected', String(+b.dataset.i === index));
    }
    el.querySelector('#op-blurb').textContent = list[index].blurb;
    stage.show(list[index], { ffa });
  };
  paint();

  el.querySelector('#op-list').addEventListener('click', (ev) => {
    const b = ev.target.closest('.op-slot');
    if (!b) return;
    index = +b.dataset.i;
    paint();
  });

  return new Promise((resolve) => {
    const done = () => {
      localStorage.setItem(storeKey, String(index));
      // The GL context goes back BEFORE the game builds its own. Browsers cap
      // live contexts at about sixteen and silently kill the oldest, so a menu
      // that leaks one costs the game a context for the rest of the session.
      stage.dispose();
      el.remove();
      resolve({ op: index, operator: list[index] });
    };
    el.querySelector('#op-go').addEventListener('click', done);
    el.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') done();
      else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { index = (index + 1) % list.length; paint(); }
      else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') { index = (index + list.length - 1) % list.length; paint(); }
    });
    el.tabIndex = 0;
    el.focus();
  });
}

function mapSelect(container) {
  return cardSelect(container, {
    title: 'OPERATION BLACKOUT',
    sub: 'SELECT DEPLOYMENT ZONE',
    items: MAPS,
    cta: 'SELECT',
  });
}

/* ------------------------------------------------------------- boot ----- */

const container = document.getElementById('app');
const q = new URLSearchParams(location.search);

// `?map=<id>` skips the picker, which is what the capture harness and every
// probe rely on. `?map=` ALONE must keep meaning "deathmatch, no extra screens"
// — that is what lets the whole existing test suite stay untouched.
const presetMap = MAPS.find((m) => m.id === q.get('map'));
const presetMode = MODES.find((m) => m.id === q.get('mode'));
const presetTeam = TEAMS.find((t) => t.id === q.get('team'));

/**
 * The default server, so "Multiplayer" just works for the people you invite.
 * Overridable in the browser screen, and remembered once changed.
 */
const DEFAULT_SERVER = 'wss://uvn-243-228.tll07.zonevs.eu:9443';

const presetNet = q.get('net') === '1';
const wantsOnline = presetMap ? presetNet
  : (await cardSelect(container, {
    title: 'OPERATION BLACKOUT', sub: 'SINGLE PLAYER OR MULTIPLAYER', items: OPPONENTS, cta: 'SELECT',
  })).item.id === 'online';

let map = null;
let mode = null;
let team = presetTeam ?? TEAMS[1];
let net = null;
/**
 * Operator slot. `?op=` pins it for captures; otherwise the picker sets it, and
 * a preset map skips the picker entirely so every existing probe still boots
 * straight into the world.
 */
let op = Number(q.get('op'));
if (!Number.isFinite(op) || op < 0) op = 0;

if (wantsOnline) {
  // ONLINE: browse rooms -> password -> lobby -> side. The room decides the map
  // and the mode, so none of the single-player screens appear.
  const gate = await masterGate(container, DEFAULT_SERVER);
  browsing: for (;;) {
    if (!gate) break;                         // backed out to single player
    const picked = await roomSelect(container, DEFAULT_SERVER, gate.master, gate.rooms);
    if (!picked) break;                       // backed out to single player

    for (;;) {
      const creds = await roomPassword(container, picked.room);
      if (!creds) break;                      // back to the room list
      creds.status('CONNECTING…');
      localStorage.setItem('blackout.name', creds.name);
      const client = new NetClient({
        url: picked.url, roomId: picked.room.id,
        master: gate.master, password: creds.password, name: creds.name,
      });
      try {
        await client.connect();
        creds.dismiss();
        picked.dismiss();
        net = client;
        map = MAPS.find((m) => m.id === client.serverMap) ?? MAPS[0];
        mode = MODES.find((m) => m.id === (client.serverMode === 'bomb' ? 'bomb' : 'dm')) ?? MODES[0];
        const side = await lobbySelect(container, client);
        team = TEAMS.find((t) => t.id === side.team) ?? TEAMS[0];
        side.dismiss();
        // Online team deathmatch dresses both sides in team colours too, so the
        // pick is from that side's six there as well — `ffa` here means "wears
        // its own colours", not "free-for-all targeting".
        // NOT `picked` — that name is already the chosen ROOM in this block,
        // and shadowing it put the room in the temporal dead zone, so
        // `picked.dismiss()` two lines above threw and every connection came
        // back to the password screen as if the server had refused it.
        const chosenOp = await operatorSelect(container, {
          side: team.id,
          ffa: false,
          sub: `${client.serverMode === 'bomb' ? 'BOMB DEFUSAL' : 'TEAM DEATHMATCH'} · ${team.id === 'red' ? 'ATTACKERS' : 'DEFENDERS'}`,
        });
        op = chosenOp.op;
        // Told to the server so everyone else builds the right body. Sent
        // rather than assumed: the join already happened, in the lobby.
        client.setOperator(op);
        break browsing;
      } catch (err) {
        creds.status({
          'bad-master': 'THE SERVER PASSWORD WAS REJECTED — GO BACK AND RE-ENTER IT',
          'bad-password': 'WRONG ROOM PASSWORD',
          throttled: 'TOO MANY ATTEMPTS — WAIT AND RETRY',
          full: 'SERVER IS FULL',
          'room-closed': 'THAT ROOM WAS CLOSED',
          unreachable: 'CANNOT REACH SERVER',
          refused: 'CONNECTION REFUSED',
        }[client.error] ?? `FAILED: ${client.error ?? err.message}`);
      }
    }
  }
}

if (!map) {
  // SINGLE PLAYER: exactly the chain that existed before multiplayer.
  map = presetMap ?? (await mapSelect(container)).item;
  mode = presetMode ?? (presetMap ? MODES[0] : (await cardSelect(container, {
    title: map.name,
    sub: 'SELECT GAME MODE',
    items: MODES.map((m) => ({
      ...m,
      disabled: m.id === 'bomb' && map.hasBombSites === false,
    })),
    cta: 'SELECT',
  })).item);

  if (mode.id === 'bomb' && !presetTeam) {
    team = presetMap ? TEAMS[0] : (await cardSelect(container, {
      title: 'BOMB DEFUSAL',
      sub: 'CHOOSE YOUR SIDE',
      items: TEAMS,
      cta: 'DEPLOY',
      keep: true,
    })).item;
  }

  // `?map=` alone still means "deathmatch, no extra screens" — the whole
  // existing probe suite depends on booting without a click.
  if (!presetMap) {
    const ffa = mode.id !== 'bomb';
    op = (await operatorSelect(container, {
      side: team.id,
      ffa,
      sub: ffa ? `${map.name} · DEATHMATCH` : `${map.name} · ${team.id === 'red' ? 'ATTACKERS' : 'DEFENDERS'}`,
    })).op;
  }
}

let game;
try {
  game = await Game.create(container, map, {
    mode: mode.id, team: team.id, op, net,
  });
} catch (err) {
  container.innerHTML = `<div class="ms-error">FAILED TO LOAD ${map.name}<br><small>${err}</small></div>`;
  throw err;
}
// Any overlay still standing (the last screen keeps itself up while loading).
document.querySelectorAll('.mapselect').forEach((n) => n.remove());

/**
 * Hooks used by tools/shot.mjs to pose the camera for visual regression
 * captures. Deliberately tiny and side-effect free apart from the pose itself.
 */
window.__WEAPONS = WEAPONS;

/**
 * Authoring hook for hand-placed bomb sites. Stand where the site should be and
 * call `__game.markSite()` in the console; paste the printed array into the
 * level's `static bombSites`. Hand-editing an AABB blind is the slowest part of
 * adding a map, so this makes it one command per site.
 */
function markSite(halfW = 4.5, halfD = 4.5, height = 3.0) {
  const p = game.player.position;
  const feet = p.y - 1.72;
  const box = [
    +(p.x - halfW).toFixed(2), +feet.toFixed(2), +(p.z - halfD).toFixed(2),
    +(p.x + halfW).toFixed(2), +(feet + height).toFixed(2), +(p.z + halfD).toFixed(2),
  ];
  console.log(`{ label: 'A', box: [${box.join(', ')}] },`);
  return box;
}
/**
 * Snapshot of everything that could explain a rendering artefact.
 *
 * Written for the "flickering black square" report, which could not be
 * reproduced headlessly across four maps, both device pixel ratios, live
 * resizes, every post-processing pass toggled and every effects layer hidden.
 * Guessing further is not measurement, so this dumps the state instead: press
 * F9 while the artefact is on screen and the whole thing lands in the console
 * and on the clipboard.
 *
 * The two prime suspects it is built to catch:
 *   - a size disagreement between the drawing buffer, the CSS box, the
 *     composer's targets and the HUD (a mismatched viewport is the only thing
 *     that draws a hard-edged rectangle nothing else knows about), and
 *   - the sun's shadow frustum, which is a SQUARE box that follows the player;
 *     everything inside it going fully dark would look exactly like the report.
 */
function diagnostics() {
  const r = game.engine.renderer;
  const c = r.domElement;
  const fx = game.postfx;
  const atmo = game.atmosphere;
  const cam = game.engine.camera;
  const d = {
    when: new Date().toISOString(),
    map: new URLSearchParams(location.search).get('map')
      ?? game.level?.constructor?.name ?? null,
    window: { inner: [innerWidth, innerHeight], dpr: devicePixelRatio },
    canvas: {
      drawingBuffer: [c.width, c.height],
      css: [c.clientWidth, c.clientHeight],
      rendererDpr: r.getPixelRatio(),
    },
    hud: game.hud ? { w: game.hud.w, h: game.hud.h, scale: game.hud.s,
      canvas: [game.hud.canvas.width, game.hud.canvas.height] } : null,
    viewport: r.getViewport(new THREE.Vector4()).toArray(),
    scissor: { test: r.getScissorTest(), box: r.getScissor(new THREE.Vector4()).toArray() },
    camera: {
      pos: cam.position.toArray().map((v) => +v.toFixed(2)),
      aspect: +cam.aspect.toFixed(4), fov: cam.fov, near: cam.near, far: cam.far,
    },
    player: {
      pos: game.player.position.toArray().map((v) => +v.toFixed(2)),
      alive: game.player.alive, health: game.player.health,
    },
  };
  if (fx?.composer) {
    const t = fx.composer.renderTarget1;
    d.composer = { size: [t.width, t.height] };
    d.passes = fx.composer.passes.map((p) => ({
      type: p.constructor.name, enabled: p.enabled,
    }));
  }
  if (atmo?.sun) {
    const sc = atmo.sun.shadow.camera;
    d.sunShadow = {
      // A directional shadow camera is a square box centred just ahead of the
      // player. If the artefact is this, its on-screen size will track
      // `extent` and its centre will track `target`.
      extent: atmo.shadowExtent,
      frustum: [sc.left, sc.right, sc.top, sc.bottom, sc.near, sc.far],
      mapSize: [atmo.sun.shadow.mapSize.x, atmo.sun.shadow.mapSize.y],
      lightPos: atmo.sun.position.toArray().map((v) => +v.toFixed(2)),
      target: atmo.sun.target.position.toArray().map((v) => +v.toFixed(2)),
      sunDir: atmo.sunDir.toArray().map((v) => +v.toFixed(3)),
      distToTarget: +atmo.sun.position.distanceTo(atmo.sun.target.position).toFixed(2),
    };
  }
  d.gl = { contextLost: r.getContext().isContextLost() };
  return d;
}

/** Toggles that let a player bisect the artefact themselves, live. */
const diagToggles = {
  shadows(on) {
    const r = game.engine.renderer;
    r.shadowMap.enabled = on ?? !r.shadowMap.enabled;
    game.engine.scene.traverse((o) => { if (o.isMesh) o.material && (o.material.needsUpdate = true); });
    return `shadowMap.enabled = ${r.shadowMap.enabled}`;
  },
  clouds(on) {
    const c = game.atmosphere?.clouds;
    if (!c) return 'no cloud deck';
    c.visible = on ?? !c.visible;
    return `clouds.visible = ${c.visible}`;
  },
  post(on) {
    const fx = game.postfx;
    for (const p of fx.composer.passes) {
      if (p.constructor.name === 'RenderPass' || p.constructor.name === 'OutputPass') continue;
      p.enabled = on ?? !p.enabled;
    }
    return fx.composer.passes.map((p) => `${p.constructor.name}:${p.enabled}`).join(' ');
  },
};

addEventListener('keydown', (e) => {
  if (e.code !== 'F9') return;
  const d = diagnostics();
  const text = JSON.stringify(d, null, 2);
  console.log('%c--- OPERATION BLACKOUT diagnostics ---', 'color:#ffb020');
  console.log(text);
  navigator.clipboard?.writeText(text).then(
    () => console.log('(copied to clipboard)'),
    () => console.log('(clipboard blocked — copy the JSON above)')
  );
});

// `?watch=1` arms the artefact watchdog for the whole session. It costs a GPU
// stall per frame, so it is opt-in rather than always on.
if (new URLSearchParams(location.search).get('watch') === '1') game._installWatchdog();

window.__game = {
  ready: true,
  game,
  // Handed out so a probe can build a Raycaster or a Box3 in page context. It
  // must be THIS module instance: a probe that imports its own copy gets a
  // second three.js whose classes fail every instanceof check inside the game.
  THREE,
  WEAPONS,
  // The rule table, so a probe asserts against the SAME numbers the game reads
  // rather than against a copy of them written down in the probe.
  BOMB_RULES,
  // Same reasoning for the difficulty table: `difficulty-probe` must walk the
  // presets the game actually ships, not a list copied into the probe that can
  // silently fall behind.
  DIFFICULTIES,
  // `duel-probe` stamps a DIFFERENT preset on each half of the bot population,
  // which `setDifficulty` cannot express — it is one setting for the whole
  // match by design. Handing out the per-bot primitive is what makes a
  // preset-vs-preset duel measurable at all.
  applySkill,
  markSite,
  diagnostics,
  diag: diagToggles,
  /** Arm the artefact watchdog by hand, for a session already in progress. */
  watch() { game._installWatchdog(); return 'watchdog armed'; },
  debugPose({ pos, yaw, pitch, ads, enemyAt, weapon, prompt = false }) {
    game.paused = true;
    // Optional weapon switch, so a capture preset can name the gun it wants
    // instead of every caller having to drive switchWeapon itself.
    if (weapon && WEAPONS[weapon]) game.switchWeapon(WEAPONS[weapon]);
    // The deploy overlay draws a full-screen dark scrim. Leaving it up during
    // captures makes every frame look two stops underexposed, so it is off by
    // default and only enabled for shots that are explicitly of the menu.
    game.hidePrompt = !prompt;
    game.player.teleport(pos[0], pos[1] - 1.72, pos[2]);
    game.player.yaw = yaw;
    game.player.pitch = pitch;
    game.player.velocity.set(0, 0, 0);
    game.weapon.ads = ads ? 1 : 0;
    game.engine.camera.fov = ads ? 80 * game.weapon.spec.fovZoom : 80;
    game.engine.camera.updateProjectionMatrix();

    if (enemyAt) {
      const e = game.enemies[0];
      if (e) {
        e.capsule.start.set(enemyAt[0], enemyAt[1] + 0.34, enemyAt[2]);
        e.capsule.end.set(enemyAt[0], enemyAt[1] + 1.62, enemyAt[2]);
        e.root.position.set(enemyAt[0], enemyAt[1], enemyAt[2]);
        e.targetYaw = e.yaw = yaw + Math.PI;
        e.setState('engage');
      }
    }

    // Advance the simulation a few frames so poses/animation settle.
    for (let i = 0; i < 8; i++) {
      game.weapon.update(1 / 60, game.input);
      game.player.update(1 / 60, { ads: !!ads });
      game.player.yaw = yaw;
      game.player.pitch = pitch;
      for (const e of game.enemies) e.update(1 / 60);
      game.atmosphere.update(game.player.position);
    }
    game.engine.viewmodelCamera.position.set(0, 0, 0);
    game.engine.viewmodelCamera.quaternion.identity();
  },
};
